import React, { useState, useEffect, useRef } from 'react';
import { EditOutlined, EyeOutlined, PlusOutlined, MinusOutlined, DeleteOutlined, PrinterOutlined, FilePdfOutlined, FullscreenOutlined, InsertRowAboveOutlined, DragOutlined } from '@ant-design/icons';
import { Spin, Tooltip, Modal, Select } from 'antd';
import JoditEditor from 'jodit-react';
import Services from '../../network/Services';
import AddOptionModal from './AddOptionModal';
import AddFormFieldModal from './AddFormFieldModal';
import AddFormModal from './AddFormModal';
import AddInfoModal from './AddInfoModal';
import AddActionModal from './AddActionModal';

const { Option } = Select;

// const nodeTypeIcon = { Question: '❓', Form: '📋', Action: '⚡', Info: 'ℹ️' };
const nodeTypeIcon = { Question: '', Form: '', Action: '', Info: '' };

// Confirmation helper for delete operations
const confirmDelete = (entityType, entityName, onConfirm) => {
  Modal.confirm({
    title: null,
    icon: null,
    content: entityName 
      ? `Are you sure, you want to delete this ${entityName}?`
      : `Are you sure, you want to delete this ${entityType}?`,
    okText: 'Delete',
    cancelText: 'Cancel',
    centered: true,
    closable: true,
    className: 'ag-delete-confirm-modal',
    onOk: onConfirm,
    okButtonProps: {
      style: { 
        backgroundColor: '#720C20', 
        borderColor: '#720C20', 
        color: '#fff',
        minWidth: '100px',
        height: '36px',
        borderRadius: '4px'
      }
    },
    cancelButtonProps: {
      style: {
        minWidth: '100px',
        height: '36px',
        borderRadius: '4px',
        borderColor: '#720C20',
        color: '#720C20'
      }
    }
  });
};

const joditConfigSmall = {
  readonly: false,
  height: 180,
  toolbarButtonSize: 'small',
  buttons: ['bold', 'italic', 'underline', '|', 'ul', 'ol', '|', 'link', '|', 'undo', 'redo'],
  removeButtons: ['image', 'video', 'file', 'speechRecognize', 'spellcheck', 'about', 'fullsize', 'print'],
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_clear_html',
  style: { fontSize: '14px' },
};

// ─── PDF / Print helpers ──────────────────────────────────────────────────────
const buildTreeText = async (categoryId, categoryName) => {
  const lines = [];
  lines.push({ text: categoryName, level: 0, bold: true });

  // Fetch top-level steps
  const stepsRes = await Services.getStepsByCategoryId(categoryId);
  const steps = (stepsRes?.data?.data || [])?.filter(n => n.isActive !== false);

  const processNode = async (node, level) => {
    const typeLabel = node.nodeTypeName ? ` [${node.nodeTypeName}]` : '';
    lines.push({ text: `${node.name}${typeLabel}`, level, bold: false });

    // Based on node type, fetch inner content
    if (node.nodeTypeName === 'Question') {
      const qRes = await Services.getQuestionsByNodeId(node.nodeId);
      const questions = (qRes?.data?.data || [])?.filter(n => n.isActive !== false);
      for (const q of questions) {
        lines.push({ text: `Question: ${q.questionText}`, level: level + 1, bold: false });
        const optRes = await Services.getOptionsByQuestionId(q.questionId);
        const options = (optRes?.data?.data || [])?.filter(n => n.isActive !== false);
        for (const opt of options) {
          lines.push({ text: `Option: ${opt.optionText}`, level: level + 2, bold: false });
          // Fetch child steps under option
          try {
            const childRes = await Services.getStepsByParentNodeId(opt.optionId, 'OPTION');
            const childSteps = (childRes?.data?.data || [])?.filter(n => n.isActive !== false);
            for (const child of childSteps) {
              await processNode(child, level + 3);
            }
          } catch {}
        }
        // Fetch child steps under question
        try {
          const qChildRes = await Services.getStepsByParentNodeId(q.questionId, 'QUESTION');
          const qChildSteps = (qChildRes?.data?.data || [])?.filter(n => n.isActive !== false);
          for (const child of qChildSteps) {
            await processNode(child, level + 2);
          }
        } catch {}
      }
    } else if (node.nodeTypeName === 'Form') {
      try {
        const formRes = await Services.getFormByNodeId(node.nodeId);
        const data = formRes?.data?.data;
        const forms = Array.isArray(data) ? data?.filter(n => n.isActive !== false) : (data && data.formId && data.isActive !== false ? [data] : []);
        for (const form of forms) {
          lines.push({ text: `Form: ${form.formName}`, level: level + 1, bold: false });
          try {
            const fieldsRes = await Services.getFormFields(form.formId);
            const fields = (fieldsRes?.data?.data || [])?.filter(n => n.isActive !== false);
            for (const f of fields) {
              lines.push({ text: `Field: ${f.label} (${f.fieldType})`, level: level + 2, bold: false });
            }
          } catch {}
          // Child steps under form
          try {
            const formChildRes = await Services.getStepsByParentNodeId(form.formId, 'FORM');
            const formChildSteps = (formChildRes?.data?.data || [])?.filter(n => n.isActive !== false);
            for (const child of formChildSteps) {
              await processNode(child, level + 2);
            }
          } catch {}
        }
      } catch {}
    } else if (node.nodeTypeName === 'Info') {
      try {
        const infoRes = await Services.getInfoByNodeId(node.nodeId);
        const data = infoRes?.data?.data;
        const infos = Array.isArray(data) ? data?.filter(n => n.isActive !== false) : (data && data.infoId && data.isActive !== false ? [data] : []);
        for (const info of infos) {
          const desc = info.description ? info.description.replace(/<[^>]*>/g, '').trim() : '';
          lines.push({ text: `Info: ${info.title}`, level: level + 1, bold: false });
          if (desc) {
            lines.push({ text: desc, level: level + 2, bold: false });
          }
        }
      } catch {}
    } else if (node.nodeTypeName === 'Action') {
      try {
        const actionRes = await Services.getActionByNodeId(node.nodeId);
        const data = actionRes?.data?.data;
        const actions = Array.isArray(data) ? data?.filter(n => n.isActive !== false) : (data && data.actionId && data.isActive !== false ? [data] : []);
        for (const action of actions) {
          let msg = '';
          try {
            const cfg = typeof action.actionConfig === 'string' ? JSON.parse(action.actionConfig) : action.actionConfig;
            msg = cfg?.defaultMessage ? cfg.defaultMessage.replace(/<[^>]*>/g, '').trim() : '';
          } catch {}
          lines.push({ text: `Action: ${action.actionType}`, level: level + 1, bold: false });
          if (msg) {
            lines.push({ text: msg, level: level + 2, bold: false });
          }
        }
      } catch {}
    }

    // Fetch child steps under this node (parentType = NODE)
    try {
      const childRes = await Services.getStepsByParentNodeId(node.nodeId, 'NODE');
      const childSteps = (childRes?.data?.data || [])?.filter(n => n.isActive !== false);
      for (const child of childSteps) {
        await processNode(child, level + 1);
      }
    } catch {}
  };

  for (const step of steps) {
    await processNode(step, 1);
  }

  return lines;
};

const handleDownloadPdf = async (categoryId, categoryName, setLoader, onError) => {
  if (setLoader) setLoader(true);
  try {
    const res = await Services.getCategoryHierarchyPdf(categoryId);
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Try to get filename from content-disposition header, fallback to categoryName
    const disposition = res.headers?.['content-disposition'] || '';
    const fileMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"'\n]+)/i);
    a.download = fileMatch ? decodeURIComponent(fileMatch[1].trim()) : `${categoryName.replace(/[^a-zA-Z0-9 ]/g, '_')}_tree.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    if (onError) onError('Failed to download PDF.');
  } finally {
    if (setLoader) setLoader(false);
  }
};

const handlePrintTree = async (categoryId, categoryName, setLoader, onError) => {
  if (setLoader) setLoader(true);
  try {
    const treeLines = await buildTreeText(categoryId, categoryName);

    // Build HTML with proper indentation
    let html = '';
    for (let i = 0; i < treeLines?.length; i++) {
      const item = treeLines[i];
      const paddingLeft = item.level * 20;
      const fontWeight = item.bold ? 'bold' : 'normal';
      const fontSize = item.level === 0 ? '18px' : '13px';
      const prefix = item.level > 0 ? '- ' : '';
      html += `<div style="padding-left:${paddingLeft}px; font-weight:${fontWeight}; font-size:${fontSize}; margin-bottom:4px;">${prefix}${item.text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${categoryName} - Tree Structure</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            h2 { color: #720C20; margin-bottom: 5px; }
            hr { border: 1px solid #720C20; margin-bottom: 15px; }
            .tree-content { line-height: 1.8; }
          </style>
        </head>
        <body>
          <h2>${categoryName}</h2>
          <hr />
          <div class="tree-content">${html}</div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  } catch (err) {
    if (onError) onError('Failed to print tree.');
  } finally {
    if (setLoader) setLoader(false);
  }
};

// ─── Decision type auto-mapping ───────────────────────────────────────────────
const DECISION_MAP = [
  { id: 1, names: ['yes'],   codes: ['YES']   },
  { id: 2, names: ['no'],    codes: ['NO']    },
  { id: 3, names: ['email'], codes: ['EMAIL'] },
  { id: 4, names: ['text'],  codes: ['TEXT']  },
  { id: 5, names: ['call'],  codes: ['CALL']  },
];

const getDecisionTypeId = (optionText) => {
  if (!optionText) return 0;
  const normalized = optionText?.trim().toLowerCase();
  const match = DECISION_MAP?.find(d =>
    d.names?.includes(normalized) || d.codes?.includes(normalized.toUpperCase())
  );
  return match ? match.id : 0;
};

// ─── Reusable Add Node dropdown ───────────────────────────────────────────────
// Renders "Add Node ▼" button; on pick opens StepModal for the chosen type.
// parentNodeId: the node under which the new step is nested (0 = top-level).
// categoryId:   the category the step belongs to.
// onAdded:      callback after successful upsert so the parent can refresh.
const AddNodeDropdown = ({ nodeTypes, parentNodeId, parentType, categoryId, loggedUserId, onNotify, onError, onAdded }) => {
  const [dropOpen, setDropOpen]   = useState(false);
  const [stepModal, setStepModal] = useState(null); // null | { nodeType }

  const handleStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const payload = {
      nodeId:     0,
      name,
      parentId:   parentNodeId ?? 0,
      // parentType: parentNodeId ? (parentType || 'NODE') : 'CATEGORY',
      parentType: parentType || 'NODE',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      // categoryId: parentNodeId ? null : categoryId,
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      if (onNotify) onNotify('Step added.');
      if (onAdded)  onAdded();
    }
  };

  return (
    <div className='agt-node-drop-wrap'>
      <div className='agt-add-btn' onClick={() => setDropOpen(p => !p)}>
        <PlusOutlined style={{ fontSize: 10 }} /> Add Step {dropOpen ? '▲' : '▼'}
      </div>
      {dropOpen && (
        <div className='agt-node-dropdown'>
          {nodeTypes?.map(nt => (
            <div
              key={nt.value}
              className='agt-node-drop-item'
              onClick={() => { setDropOpen(false); setStepModal({ nodeType: nt }); }}
            >
              <span>{nodeTypeIcon[nt?.label] || ''}</span>
              <span>{nt?.label}</span>
            </div>
          ))}
        </div>
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={null}
          nodeTypes={nodeTypes}
          categoryId={categoryId}
        />
      )}
    </div>
  );
};

// ─── Option row ───────────────────────────────────────────────────────────────
const OptionRow = ({ opt, onEdit, nodeTypes, categoryId, loggedUserId, onNotify, onError, autoExpand }) => {
  const [open, setOpen]             = useState(false);
  const [childSteps, setChildSteps] = useState([]);
  const [loaded, setLoaded]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [stepModal, setStepModal]   = useState(null);

  const fetchChildSteps = () => {
    setLoading(true);
    Services.getStepsByParentNodeId(opt.optionId, 'OPTION')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const refresh = () => { if (loaded) fetchChildSteps(); };
    window.addEventListener('agentTreeRefresh', refresh);
    return () => window.removeEventListener('agentTreeRefresh', refresh);
  }, [loaded]);

  useEffect(() => {
    if (autoExpand && !loaded) {
      fetchChildSteps();
      setLoaded(true);
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    if (!loaded) { fetchChildSteps(); setLoaded(true); }
    setOpen(p => !p);
  };

  const handleStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   opt.optionId,
      parentType: 'OPTION',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      if (onNotify) onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNode = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        if (onNotify) onNotify('Step deleted.');
      }
    } catch {
      if (onError) onError('Failed to delete step.');
    }
  };

  return (
    <div className='agt-option-block'>
      <div className='agt-row agt-option-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-option-text' onClick={handleToggle} style={{ cursor: 'pointer' }}>{opt.optionText}</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={() => onEdit(opt)} />
        </Tooltip>
      </div>
      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {childSteps?.length === 0 && !loading && <p className='agt-empty' style={{ marginTop: 8 }}>No next steps yet.</p>}
          {childSteps?.length === 0 && (
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={opt.optionId}
              parentType='OPTION'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              onAdded={fetchChildSteps}
            />
          )}
          {childSteps?.map(node => (
            <StepRow
              key={node.nodeId}
              node={node}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              isTopLevel={true}
              autoExpand={autoExpand}
              onEdit={(n) => {
                const nt = nodeTypes?.find(t => Number(t.value) === n?.nodeTypeId)
                  || { label: n?.nodeTypeName || 'Node', value: String(n?.nodeTypeId) };
                setStepModal({ editData: n, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}
          </Spin>
        </div>
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
          categoryId={categoryId}
          onDelete={handleDeleteNode}
        />
      )}
    </div>
  );
};

// ─── Question row ─────────────────────────────────────────────────────────────
const QuestionRow = ({ q, onEdit, nodeTypes, categoryId, loggedUserId, onNotify, onError, parentNodeId, autoExpand }) => {
  const [open, setOpen]         = useState(false);
  const [options, setOptions]   = useState([]);
  const [childSteps, setChildSteps] = useState([]);
  const [loaded, setLoaded]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [optModal, setOptModal] = useState(null); // null | { editData }
  const [stepModal, setStepModal] = useState(null);

  const fetchOptions = () => {
    setLoading(true);
    Services.getOptionsByQuestionId(q.questionId)
      .then(res => setOptions((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const fetchChildSteps = () => {
    Services.getStepsByParentNodeId(q.questionId, 'QUESTION')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    const refresh = () => { if (loaded) { fetchOptions(); fetchChildSteps(); } };
    window.addEventListener('agentTreeRefresh', refresh);
    return () => window.removeEventListener('agentTreeRefresh', refresh);
  }, [loaded]);

  useEffect(() => {
    if (autoExpand && !loaded) {
      fetchOptions();
      fetchChildSteps();
      setLoaded(true);
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    if (!loaded) { fetchOptions(); fetchChildSteps(); setLoaded(true); }
    setOpen(p => !p);
  };

  const handleOptionSubmit = async (optionData) => {
    const { editData } = optModal;
    const payload = {
      optionId:       editData ? editData.optionId : 0,
      questionId:     q.questionId,
      optionText:     optionData.optionText,
      description:    optionData.description || null,
      displayType:    optionData.displayType || 'BUTTON',
      badge:          optionData.badge || null,
      badgeColor:     optionData.badgeColor || null,
      styleClass:     optionData.styleClass || null,
      icon:           optionData.icon || null,
      nextNodeId:     optionData.nextNodeId != null ? optionData.nextNodeId : null,
      orderNo:        0,
      isActive:       true,
      decisionTypeId: getDecisionTypeId(optionData.optionText),
      docTypeId:      optionData.docTypeId || null,
      headerButton:   optionData.headerButton || null,
      link:           optionData.link || null,
      elements:       optionData.elements || [],
      upsertedBy:     loggedUserId,
    };
    const res = await Services.upsertAgentOption(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to save option.';
      if (onError) onError(msg);
      throw new Error(msg);
    }
    if (res?.data) { setOptModal(null); fetchOptions(); }
  };

  const handleDeleteOption = async (optionId) => {
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'option', entityId: optionId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) { setOptModal(null); fetchOptions(); if (onNotify) onNotify('Option deleted.'); }
    } catch { if (onError) onError('Failed to delete option.'); }
  };

  const handleStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   q.questionId,
      parentType: 'QUESTION',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      if (onNotify) onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNode = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        if (onNotify) onNotify('Step deleted.');
      }
    } catch {
      if (onError) onError('Failed to delete step.');
    }
  };

  return (
    <div className='agt-question-block'>
      <div className='agt-row agt-question-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-question-text' onClick={handleToggle} style={{ cursor: 'pointer' }}>{q.questionText}</span>
        <span className='agt-badge'>{q.questionTypeName}</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={() => onEdit(q)} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {options?.length === 0 && !loading && <p className='agt-empty'>No options yet.</p>}
          {options?.map(opt => (
            <OptionRow
              key={opt.optionId}
              opt={opt}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              autoExpand={autoExpand}
              onEdit={(o) => setOptModal({ editData: o })}
            />
          ))}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div className='agt-add-btn' onClick={() => setOptModal({ editData: null })}>
              <PlusOutlined style={{ fontSize: 10 }} /> Add Option
            </div>
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={q.questionId}
              parentType='QUESTION'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              onAdded={fetchChildSteps}
            />
          </div>

          {/* Child steps under this question */}
          {childSteps?.map(node => (
            <StepRow
              key={node.nodeId}
              node={node}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              isTopLevel={true}
              autoExpand={autoExpand}
              onEdit={(n) => {
                const nt = nodeTypes?.find(t => Number(t.value) === n?.nodeTypeId)
                  || { label: n?.nodeTypeName || 'Node', value: String(n?.nodeTypeId) };
                setStepModal({ editData: n, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}
          </Spin>
        </div>
      )}

      <AddOptionModal
        visible={!!optModal}
        onClose={() => setOptModal(null)}
        onSubmit={handleOptionSubmit}
        editData={optModal?.editData}
        onDelete={handleDeleteOption}
        categoryId={categoryId}
      />
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
          categoryId={categoryId}
          onDelete={handleDeleteNode}
        />
      )}
    </div>
  );
};

// ─── Step / Node row ──────────────────────────────────────────────────────────
const StepRow = ({ node, nodeTypes, onEdit, onStepAdded, onInsertBefore, categoryId, loggedUserId, onNotify, onError, isTopLevel, autoExpand }) => {
  const isForm   = node.nodeTypeName === 'Form';
  const isInfo   = node.nodeTypeName === 'Info';
  const isAction = node.nodeTypeName === 'Action';

  const [insertModal, setInsertModal] = useState(null);

  const handleInsertBeforeSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    // Step 1: Create new node with same parentId/parentType as current node
    const newPayload = {
      nodeId:     0,
      name,
      parentId:   node.parentId,
      parentType: node.parentType,
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(newPayload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to insert step.';
      if (onError) onError(msg);
      return;
    }
    // Extract new node ID from response — handle various response shapes
    const resData = res?.data?.data || res?.data;
    const newNodeId = resData?.nodeId || resData?.NodeId || resData?.id || resData?.Id
      || (Array.isArray(resData) && resData[0] ? (resData[0].nodeId || resData[0].NodeId) : null);

    if (!newNodeId) {
      // Step created but couldn't get ID — close modal and refresh
      console.warn('Insert Before: Could not extract nodeId from response', res?.data);
      setInsertModal(null);
      if (onNotify) onNotify('Step created but could not link. Please rearrange manually.');
      if (onStepAdded) onStepAdded();
      return;
    }
    // Step 2: Update current node's parentId to new node, parentType to NODE
    const parsedButtonsForUpdate = typeof node.buttons === 'string'
      ? (() => { try { return JSON.parse(node.buttons); } catch { return []; } })()
      : (node.buttons || []);
    
    // Normalize buttons to include link and isFooter fields
    const normalizedButtons = parsedButtonsForUpdate.map(b => ({
      buttonId: b.ButtonId || b.buttonId || 0,
      buttonText: b.ButtonText || b.buttonText || '',
      buttonType: b.ButtonType || b.buttonType || null,
      nextNodeId: b.NextNodeId || b.nextNodeId || null,
      orderNo: b.OrderNo || b.orderNo || null,
      isActive: b.isActive !== false && b.IsActive !== false,
      link: b.Link || b.link || null,
      isFooter: !!(b.IsFooter || b.isFooter),
    }));
    
    const parsedHeaderForUpdate = (() => {
      if (!node.header) return null;
      try {
        const h = typeof node.header === 'string' ? JSON.parse(node.header) : node.header;
        // If header has a button with ApiLogDetails as string, parse it to object
        if (h && h.Button && h.Button.ApiLogDetails && typeof h.Button.ApiLogDetails === 'string') {
          try {
            h.Button.ApiLogDetails = JSON.parse(h.Button.ApiLogDetails);
          } catch {
            // If parse fails, leave as is
          }
        }
        // Also handle camelCase version
        if (h && h.button && h.button.apiLogDetails && typeof h.button.apiLogDetails === 'string') {
          try {
            h.button.apiLogDetails = JSON.parse(h.button.apiLogDetails);
          } catch {
            // If parse fails, leave as is
          }
        }
        return h;
      }
      catch { return null; }
    })();
    const updatePayload = {
      nodeId:          node.nodeId,
      name:            node.name,
      parentId:        newNodeId,
      parentType:      'NODE',
      orderNo:         0,
      nodeTypeId:      node.nodeTypeId,
      categoryId:      categoryId,
      isActive:        true,
      buttons:         normalizedButtons,
      apiLogIds:       node.apiLogIds ? node.apiLogIds?.map(item => typeof item === 'object' ? item.apiLogId : item) : [],
      paymentResource: node.paymentResource || null,
      header:          parsedHeaderForUpdate,
      sameScreenId:    node.sameScreenId || null,
      upsertedBy:      loggedUserId,
    };
    const res2 = await Services.upsertAgentStep(updatePayload);
    if (res2?.data?.succeeded === false || res2?.data?.Succeeded === false) {
      const msg = res2?.data?.messages?.[0] || res2?.data?.Messages?.[0] || 'Step created but failed to relink.';
      if (onError) onError(msg);
    } else {
      if (onNotify) onNotify('Step inserted successfully.');
    }
    setInsertModal(null);
    if (onStepAdded) onStepAdded();
  };

  const handleInsertClick = () => {
    setInsertModal({ targetNode: node });
  };

  // ─── Move To logic ────────────────────────────────────────────────────────
  const [moveModal, setMoveModal] = useState(false);
  const [moveTargetType, setMoveTargetType] = useState(undefined);
  const [moveTargetId, setMoveTargetId] = useState(undefined);
  const [moveOptions, setMoveOptions] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveTargetsLoading, setMoveTargetsLoading] = useState(false);

  const handleMoveClick = () => {
    setMoveModal(true);
    setMoveTargetType(undefined);
    setMoveTargetId(undefined);
    setMoveOptions([]);
  };

  const fetchMoveTargets = async (targetType) => {
    setMoveTargetType(targetType);
    setMoveTargetId(undefined);
    setMoveOptions([]);
    setMoveTargetsLoading(true);
    try {
      if (targetType === 'NODE') {
        const res = await Services.getAllStepsByCategoryId(categoryId);
        const data = (res?.data?.data || [])?.filter(n => n.isActive !== false && n.nodeId !== node.nodeId);
        // Simply use step data without fetching additional details
        const options = data?.map(n => ({ value: n.nodeId, label: `${n.name} (${n.nodeTypeName})` }));
        setMoveOptions(options);
      } else if (targetType === 'OPTION') {
        // Optimized: Use category-level API to get all options in one call
        const res = await Services.getOptionsByCategoryId(categoryId);
        const allOptions = (res?.data?.data || [])
          ?.filter(o => o.isActive !== false)
          ?.map(o => ({
            value: o.optionId,
            label: `${o.optionText}${o.nextNodeName ? ` → ${o.nextNodeName}` : ''}`
          }));
        setMoveOptions(allOptions);
      } else if (targetType === 'QUESTION') {
        // Optimized: Use category-level API to get all questions in one call
        const res = await Services.getQuestionsByCategoryId(categoryId);
        const allQuestions = (res?.data?.data || [])
          ?.filter(q => q.isActive !== false)
          .map(q => ({
            value: q.questionId,
            label: q.questionText
          }));
        setMoveOptions(allQuestions);
      } else if (targetType === 'CATEGORY') {
        setMoveOptions([{ value: categoryId, label: `Category (top-level)` }]);
        setMoveTargetId(categoryId);
      }
    } catch {
      setMoveOptions([]);
    } finally {
      setMoveTargetsLoading(false);
    }
  };

  const handleMoveSubmit = async () => {
    if (!moveTargetType || !moveTargetId) return;
    setMoveLoading(true);
    try {
      const parsedButtons = typeof node.buttons === 'string'
        ? (() => { try { return JSON.parse(node.buttons); } catch { return []; } })()
        : (node.buttons || []);
      
      // Normalize buttons to include link and isFooter fields
      const normalizedButtons = parsedButtons.map(b => ({
        buttonId: b.ButtonId || b.buttonId || 0,
        buttonText: b.ButtonText || b.buttonText || '',
        buttonType: b.ButtonType || b.buttonType || null,
        nextNodeId: b.NextNodeId || b.nextNodeId || null,
        orderNo: b.OrderNo || b.orderNo || null,
        isActive: b.isActive !== false && b.IsActive !== false,
        link: b.Link || b.link || null,
        isFooter: !!(b.IsFooter || b.isFooter),
      }));
      
      const parsedHeader = (() => {
        if (!node.header) return null;
        try {
          const h = typeof node.header === 'string' ? JSON.parse(node.header) : node.header;
          // If header has a button with ApiLogDetails as string, parse it to object
          if (h && h.Button && h.Button.ApiLogDetails && typeof h.Button.ApiLogDetails === 'string') {
            try {
              h.Button.ApiLogDetails = JSON.parse(h.Button.ApiLogDetails);
            } catch {
              // If parse fails, leave as is
            }
          }
          // Also handle camelCase version
          if (h && h.button && h.button.apiLogDetails && typeof h.button.apiLogDetails === 'string') {
            try {
              h.button.apiLogDetails = JSON.parse(h.button.apiLogDetails);
            } catch {
              // If parse fails, leave as is
            }
          }
          return h;
        }
        catch { return null; }
      })();
      const payload = {
        nodeId:          node.nodeId,
        name:            node.name,
        parentId:        moveTargetId,
        parentType:      moveTargetType,
        orderNo:         0,
        nodeTypeId:      node.nodeTypeId,
        categoryId:      categoryId,
        isActive:        true,
        buttons:         normalizedButtons,
        apiLogIds:       node.apiLogIds ? node.apiLogIds?.map(item => typeof item === 'object' ? item.apiLogId : item) : [],
        paymentResource: node.paymentResource || null,
        header:          parsedHeader,
        sameScreenId:    node.sameScreenId || null,
        upsertedBy:      loggedUserId,
      };
      const res = await Services.upsertAgentStep(payload);
      if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
        const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to move step.';
        if (onError) onError(msg);
      } else {
        if (onNotify) onNotify('Step moved successfully.');
        if (onStepAdded) onStepAdded();
        // Dispatch event to refresh all open category trees
        window.dispatchEvent(new CustomEvent('agentTreeRefresh'));
      }
    } catch {
      if (onError) onError('Failed to move step.');
    } finally {
      setMoveLoading(false);
      setMoveModal(false);
    }
  };

  // Move To Modal (shared across all step types)
  const moveToModal = moveModal ? (
    <Modal
      title={<span style={{ fontSize: 16, color: '#474747', fontWeight: "bold" }}>Move Step: {node.name}</span>}
      visible={moveModal}
      onCancel={() => setMoveModal(false)}
      footer={null}
      centered
      destroyOnClose
      className="ag-modal"
      bodyStyle={{ padding: '20px 28px 28px', borderTop: "1px solid #bfbfbf" }}
    >
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>Move To (Parent Type)</label>
        <select
          value={moveTargetType || ''}
          onChange={(e) => { if (e.target.value) fetchMoveTargets(e.target.value); else { setMoveTargetType(undefined); setMoveOptions([]); } }}
          style={{ width: '100%', height: 38, borderRadius: 4, border: '1px solid #720C20', padding: '0 10px', fontSize: 14 }}
        >
          <option value="">Select parent type</option>
          <option value="NODE">Node (Step)</option>
          <option value="OPTION">Option</option>
          <option value="QUESTION">Question</option>
          <option value="CATEGORY">Category (top-level)</option>
        </select>
      </div>
      {moveTargetType && moveTargetType !== 'CATEGORY' && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>Select Target</label>
          {moveTargetsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
              <Spin size="small" /> <span style={{ fontSize: 13, color: '#888' }}>Loading targets...</span>
            </div>
          ) : (
            <Select
              showSearch
              allowClear
              placeholder="Select target"
              value={moveTargetId}
              onChange={(val) => setMoveTargetId(val)}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              style={{ width: '100%' }}
              size="large"
            >
              {moveOptions?.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <button onClick={() => setMoveModal(false)} style={{ padding: '8px 16px', border: '1px solid #720C20', borderRadius: 4, color: '#720C20', background: '#fff', cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
        <button onClick={handleMoveSubmit} disabled={!moveTargetType || !moveTargetId || moveLoading} style={{ padding: '8px 16px', border: 'none', borderRadius: 4, color: '#fff', background: (!moveTargetType || !moveTargetId) ? '#ccc' : '#720C20', cursor: 'pointer', fontWeight: 500 }}>
          {moveLoading ? 'Moving...' : 'Move'}
        </button>
      </div>
    </Modal>
  ) : null;

  if (isForm) {
    return (
      <>
      <FormStepRow
        node={node}
        nodeTypes={nodeTypes}
        categoryId={categoryId}
        onEdit={onEdit}
        onInsertBefore={handleInsertClick}
        onMoveTo={handleMoveClick}
        loggedUserId={loggedUserId}
        onNotify={onNotify}
        onError={onError}
        isTopLevel={isTopLevel}
        autoExpand={autoExpand}
      />
      {insertModal !== null && (
        <StepModal
          visible={!!insertModal}
          onClose={() => setInsertModal(null)}
          onSubmit={handleInsertBeforeSubmit}
          nodeType={null}
          editData={null}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
        />
      )}
      {moveToModal}
      </>
    );
  }

  if (isInfo) {
    return (
      <>
      <InfoStepRow
        node={node}
        nodeTypes={nodeTypes}
        categoryId={categoryId}
        onEdit={onEdit}
        onInsertBefore={handleInsertClick}
        onMoveTo={handleMoveClick}
        loggedUserId={loggedUserId}
        onNotify={onNotify}
        onError={onError}
        isTopLevel={isTopLevel}
        autoExpand={autoExpand}
      />
      {insertModal !== null && (
        <StepModal
          visible={!!insertModal}
          onClose={() => setInsertModal(null)}
          onSubmit={handleInsertBeforeSubmit}
          nodeType={null}
          editData={null}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
        />
      )}
      {moveToModal}
      </>
    );
  }

  if (isAction) {
    return (
      <>
      <ActionStepRow
        node={node}
        nodeTypes={nodeTypes}
        categoryId={categoryId}
        onEdit={onEdit}
        onInsertBefore={handleInsertClick}
        onMoveTo={handleMoveClick}
        loggedUserId={loggedUserId}
        onNotify={onNotify}
        onError={onError}
        isTopLevel={isTopLevel}
        autoExpand={autoExpand}
      />
      {insertModal !== null && (
        <StepModal
          visible={!!insertModal}
          onClose={() => setInsertModal(null)}
          onSubmit={handleInsertBeforeSubmit}
          nodeType={null}
          editData={null}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
        />
      )}
      {moveToModal}
      </>
    );
  }

  return (
    <>
    <QuestionStepRow
      node={node}
      nodeTypes={nodeTypes}
      categoryId={categoryId}
      onEdit={onEdit}
      onInsertBefore={handleInsertClick}
      onMoveTo={handleMoveClick}
      loggedUserId={loggedUserId}
      onNotify={onNotify}
      onError={onError}
      isTopLevel={isTopLevel}
      autoExpand={autoExpand}
    />
    {insertModal !== null && (
      <StepModal
        visible={!!insertModal}
        onClose={() => setInsertModal(null)}
        onSubmit={handleInsertBeforeSubmit}
        nodeType={null}
        editData={null}
        nodeTypes={nodeTypes}
 categoryId={categoryId}
      />
    )}
    {moveToModal}
    </>
  );
};

// ─── Form step row (nodeTypeId = 2) ──────────────────────────────────────────
const FormStepRow = ({ node, nodeTypes, categoryId, onEdit, onInsertBefore, onMoveTo, loggedUserId, onNotify, onError, isTopLevel, autoExpand }) => {
  const [open, setOpen]             = useState(false);
  const [forms, setForms]           = useState([]);
  const [childSteps, setChildSteps] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [formModal, setFormModal]   = useState(null);
  const [stepModal, setStepModal]   = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const fetchForm = () => {
    Services.getFormByNodeId(node.nodeId)
      .then(res => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setForms(data?.filter(n => n.isActive !== false));
        else if (data && data.formId && data.isActive !== false) setForms([data]);
        else setForms([]);
      })
      .catch(() => {});
  };

  const fetchChildSteps = () => {
    Services.getStepsByParentNodeId(node.nodeId, 'NODE')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    if (autoExpand) {
      setLoading(true);
      Promise.all([
        Services.getFormByNodeId(node.nodeId).then(res => {
          const data = res?.data?.data;
          if (Array.isArray(data)) setForms(data?.filter(n => n.isActive !== false));
          else if (data && data.formId && data.isActive !== false) setForms([data]);
          else setForms([]);
        }),
        Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    setLoading(true);
    Promise.all([
      Services.getFormByNodeId(node.nodeId).then(res => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setForms(data?.filter(n => n.isActive !== false));
        else if (data && data.formId && data.isActive !== false) setForms([data]);
        else setForms([]);
      }),
      Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
    ]).catch(() => {}).finally(() => setLoading(false));
    setOpen(p => !p);
  };

  const handleFormSubmit = async ({ formName, title, titleBackgroundColor, subTitle, subTitleBackgroundColor, description, descriptionBackgroundColor, subDescription, subDescriptionBackgroundColor }) => {
    const { editData } = formModal;
    const payload = {
      formId:                        editData ? editData.formId : 0,
      nodeId:                        node.nodeId,
      formName,
      title:                         title || null,
      titleBackgroundColor:          titleBackgroundColor || null,
      subTitle:                      subTitle || null,
      subTitleBackgroundColor:       subTitleBackgroundColor || null,
      description:                   description || null,
      descriptionBackgroundColor:    descriptionBackgroundColor || null,
      subDescription:                subDescription || null,
      subDescriptionBackgroundColor: subDescriptionBackgroundColor || null,
      nextNodeId:                    0,
      isActive:                      true,
      upsertedBy:                    loggedUserId,
    };
    const res = await Services.upsertForm(payload);
    if (res?.data) {
      setFormModal(null);
      fetchForm();
      setOpen(true);
      onNotify(editData ? 'Form updated.' : 'Form added.');
    }
  };

  const handleChildStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   node.nodeId,
      parentType: 'NODE',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNodeInForm = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        onNotify('Step deleted.');
      }
    } catch {
      onError('Failed to delete step.');
    }
  };

  const handleDeleteForm = async (formId) => {
    const form = forms.find(f => f.formId === formId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'form', entityId: formId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) { setFormModal(null); fetchForm(); onNotify('Form deleted.'); }
    } catch { onError('Failed to delete form.'); }
  };

  return (
    <div className='agt-step-block'>
      <div className='agt-row agt-step-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-step-icon'>{nodeTypeIcon[node?.nodeTypeName] || ''}</span>
        <span className='agt-step-name' onClick={handleToggle} style={{ cursor: 'pointer' }}>{node?.name}</span>
        <span className='agt-badge'>{node?.nodeTypeName}</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={() => onEdit(node)} />
        </Tooltip>
        <Tooltip title="Insert Before">
          <InsertRowAboveOutlined className='agt-edit-icon' onClick={() => onInsertBefore && onInsertBefore(node)} />
        </Tooltip>
        <Tooltip title="Move To">
          <DragOutlined className='agt-edit-icon' onClick={() => onMoveTo && onMoveTo(node)} />
        </Tooltip>
        <Tooltip title="Preview">
          <EyeOutlined className='agt-edit-icon agt-preview-icon' onClick={() => setPreviewVisible(true)} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {forms?.length === 0 && !loading && <p className='agt-empty'>No forms yet.</p>}
          {forms?.map(form => (
            <FormRow
              key={form.formId}
              form={form}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              autoExpand={autoExpand}
              onEdit={() => setFormModal({ editData: form })}
            />
          ))}
          {forms?.length === 0 && (
            <div className='agt-add-btn' onClick={() => setFormModal({ editData: null })}>
              <PlusOutlined style={{ fontSize: 10 }} /> Add Form
            </div>
          )}

          {/* Child steps nested under this form node */}
          {childSteps?.map(n => (
            <StepRow
              key={n.nodeId}
              node={n}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              autoExpand={autoExpand}
              onEdit={(nd) => {
                const nt = nodeTypes?.find(t => Number(t.value) === nd?.nodeTypeId)
                  || { label: nd?.nodeTypeName || 'Node', value: String(nd?.nodeTypeId) };
                setStepModal({ editData: nd, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}

          {!isTopLevel && (
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={node.nodeId}
              parentType='NODE'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              onAdded={fetchChildSteps}
            />
          )}
          </Spin>
        </div>
      )}

      {formModal !== null && (
        <AddFormModal
          visible={!!formModal}
          onClose={() => setFormModal(null)}
          onSubmit={handleFormSubmit}
          editData={formModal?.editData}
          onDelete={handleDeleteForm}
        />
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleChildStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
          onDelete={handleDeleteNodeInForm}
        />
      )}
      <NodePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        node={node}
      />
    </div>
  );
};

// ─── Form row (shows form name + its fields + child steps) ───────────────────
const FormRow = ({ form, nodeTypes, categoryId, loggedUserId, onNotify, onError, onEdit, autoExpand }) => {
  const [open, setOpen]             = useState(false);
  const [fields, setFields]         = useState([]);
  const [childSteps, setChildSteps] = useState([]);
  const [loaded, setLoaded]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [fieldModal, setFieldModal] = useState(null);
  const [stepModal, setStepModal]   = useState(null);

  const fetchFields = () => {
    Services.getFormFields(form.formId)
      .then(res => setFields((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  const fetchChildSteps = () => {
    Services.getStepsByParentNodeId(form.formId, 'FORM')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    if (autoExpand && !loaded) {
      setLoading(true);
      Promise.all([
        Services.getFormFields(form.formId).then(res => setFields((res?.data?.data || [])?.filter(n => n.isActive !== false))),
        Services.getStepsByParentNodeId(form.formId, 'FORM').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setLoaded(true);
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    if (!loaded) {
      setLoading(true);
      Promise.all([
        Services.getFormFields(form.formId).then(res => setFields((res?.data?.data || [])?.filter(n => n.isActive !== false))),
        Services.getStepsByParentNodeId(form.formId, 'FORM').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setLoaded(true);
    }
    setOpen(p => !p);
  };

  const handleFieldSubmit = async ({ label, fieldType, formUniqueLabel, fieldOptions }) => {
    const { editData } = fieldModal;
    const payload = {
      fieldId:         editData ? editData?.fieldId : 0,
      formId:          form?.formId,
      label,
      formUniqueLabel: formUniqueLabel || '',
      fieldType,
      isRequired:      false,
      orderNo:         0,
      isActive:        true,
      fieldOptions:    fieldOptions || [],
      upsertedBy:      loggedUserId,
    };
    const res = await Services.upsertFormField(payload);
    if (res?.data) {
      setFieldModal(null);
      fetchFields();
      onNotify(editData ? 'Field updated.' : 'Field added.');
    }
  };

  const handleChildStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   form.formId,
      parentType: 'FORM',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNodeInFormRow = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        onNotify('Step deleted.');
      }
    } catch {
      onError('Failed to delete step.');
    }
  };

  const handleDeleteField = async (fieldId) => {
    const field = fields.find(f => f.fieldId === fieldId);
    try {
      const res = await Services.deleteFieldByFieldId({ FieldId: fieldId, DeletedBy: loggedUserId, UserId: loggedUserId });
      if (res?.data) { setFieldModal(null); fetchFields(); onNotify('Field deleted.'); }
    } catch { onError('Failed to delete field.'); }
  };

  return (
    <div className='agt-form-block'>
      <div className='agt-row agt-form-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span onClick={handleToggle} style={{color: "#444", fontWeight: 600, fontSize:"19px", cursor: 'pointer'}}>{form.formName}</span>
        <span className='agt-badge'>Form</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={onEdit} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {fields?.length === 0 && !loading && <p className='agt-empty'>No fields yet.</p>}
          {fields?.map(f => (
            <div key={f.fieldId} className='agt-row agt-field-row'>
              <span className='' style={{color:"#444", fontSize:"18px"}}>{f?.label}</span>
              <span className='agt-badge'>{f?.fieldType}</span>
              <Tooltip title="Edit">
                <EditOutlined
                  className='agt-edit-icon'
                  onClick={() => setFieldModal({ editData: f })}
                />
              </Tooltip>
            </div>
          ))}
          <div className='agt-add-btn' onClick={() => setFieldModal({ editData: null })}>
            <PlusOutlined style={{ fontSize: 10 }} /> Add Form Field
          </div>

          {/* Child steps under this form */}
          {childSteps?.map(n => (
            <StepRow
              key={n.nodeId}
              node={n}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              isTopLevel={true}
              autoExpand={autoExpand}
              onEdit={(nd) => {
                const nt = nodeTypes?.find(t => Number(t.value) === nd?.nodeTypeId)
                  || { label: nd?.nodeTypeName || 'Node', value: String(nd?.nodeTypeId) };
                setStepModal({ editData: nd, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}

          {childSteps?.length === 0 && (
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={form.formId}
              parentType='FORM'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              onAdded={fetchChildSteps}
            />
          )}
          </Spin>
        </div>
      )}

      {fieldModal !== null && (
        <AddFormFieldModal
          visible={!!fieldModal}
          onClose={() => setFieldModal(null)}
          onSubmit={handleFieldSubmit}
          editData={fieldModal?.editData}
          onDelete={handleDeleteField}
        />
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleChildStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
          onDelete={handleDeleteNodeInFormRow}
        />
      )}
    </div>
  );
};

// ─── Info step row (nodeTypeId = 4) ──────────────────────────────────────────
const InfoStepRow = ({ node, nodeTypes, categoryId, onEdit, onInsertBefore, onMoveTo, loggedUserId, onNotify, onError, isTopLevel, autoExpand }) => {
  const [open, setOpen]             = useState(false);
  const [infos, setInfos]           = useState([]);
  const [childSteps, setChildSteps] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [infoModal, setInfoModal]   = useState(null);
  const [stepModal, setStepModal]   = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const fetchInfo = () => {
    Services.getInfoByNodeId(node.nodeId)
      .then(res => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setInfos(data?.filter(n => n?.isActive !== false));
        else if (data && data?.infoId && data?.isActive !== false) setInfos([data]);
        else setInfos([]);
      })
      .catch(() => {});
  };

  const fetchChildSteps = () => {
    Services.getStepsByParentNodeId(node.nodeId, 'NODE')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n?.isActive !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    if (autoExpand) {
      setLoading(true);
      Promise.all([
        Services.getInfoByNodeId(node.nodeId).then(res => {
          const data = res?.data?.data;
          if (Array.isArray(data)) setInfos(data?.filter(n => n?.isActive !== false));
          else if (data && data.infoId && data.isActive !== false) setInfos([data]);
          else setInfos([]);
        }),
        Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    setLoading(true);
    Promise.all([
      Services.getInfoByNodeId(node.nodeId).then(res => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setInfos(data?.filter(n => n?.isActive !== false));
        else if (data && data.infoId && data.isActive !== false) setInfos([data]);
        else setInfos([]);
      }),
      Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
    ]).catch(() => {}).finally(() => setLoading(false));
    setOpen(p => !p);
  };

  const handleInfoSubmit = async ({ title, titleBackgroundColor, subTitle, subTitleBackgroundColor, description, descriptionBackgroundColor, subDescription, subDescriptionBackgroundColor }) => {
    const { editData } = infoModal;
    const payload = {
      infoId:                        editData ? editData.infoId : 0,
      nodeId:                        node.nodeId,
      title,
      titleBackgroundColor:          titleBackgroundColor || null,
      subTitle:                      subTitle || null,
      subTitleBackgroundColor:       subTitleBackgroundColor || null,
      description,
      descriptionBackgroundColor:    descriptionBackgroundColor || null,
      subDescription:                subDescription || null,
      subDescriptionBackgroundColor: subDescriptionBackgroundColor || null,
      nextNodeId:                    0,
      isActive:                      true,
      upsertedBy:                    loggedUserId,
    };
    const res = await Services.upsertInfo(payload);
    if (res?.data) {
      setInfoModal(null);
      fetchInfo();
      setOpen(true);
      onNotify(editData ? 'Info updated.' : 'Info added.');
    }
  };

  const handleChildStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   node.nodeId,
      parentType: 'NODE',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNodeInInfo = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        onNotify('Step deleted.');
      }
    } catch {
      onError('Failed to delete step.');
    }
  };

  const handleDeleteInfo = async (infoId) => {
    const info = infos.find(i => i.infoId === infoId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'info', entityId: infoId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) { setInfoModal(null); fetchInfo(); onNotify('Info deleted.'); }
    } catch { onError('Failed to delete info.'); }
  };

  return (
    <div className='agt-step-block'>
      <div className='agt-row agt-step-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-step-icon'>{nodeTypeIcon[node?.nodeTypeName] || ''}</span>
        <span className='agt-step-name' onClick={handleToggle} style={{ cursor: 'pointer' }}>{node?.name}</span>
        <span className='agt-badge'>{node?.nodeTypeName}</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={() => onEdit(node)} />
        </Tooltip>
        <Tooltip title="Insert Before">
          <InsertRowAboveOutlined className='agt-edit-icon' onClick={() => onInsertBefore && onInsertBefore(node)} />
        </Tooltip>
        <Tooltip title="Move To">
          <DragOutlined className='agt-edit-icon' onClick={() => onMoveTo && onMoveTo(node)} />
        </Tooltip>
        <Tooltip title="Preview">
          <EyeOutlined className='agt-edit-icon agt-preview-icon' onClick={() => setPreviewVisible(true)} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {infos?.length === 0 && !loading && <p className='agt-empty'>No info yet.</p>}
          {infos?.map(info => (
            <InfoRow
              key={info.infoId}
              info={info}
              autoExpand={autoExpand}
              onEdit={() => setInfoModal({ editData: info })}
            />
          ))}
          {infos?.length === 0 && (
            <div className='agt-add-btn' onClick={() => setInfoModal({ editData: null })}>
              <PlusOutlined style={{ fontSize: 10 }} /> Add Info
            </div>
          )}

          {/* Child steps nested under this info node */}
          {childSteps?.map(n => (
            <StepRow
              key={n.nodeId}
              node={n}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              autoExpand={autoExpand}
              onEdit={(nd) => {
                const nt = nodeTypes?.find(t => Number(t.value) === nd?.nodeTypeId)
                  || { label: nd?.nodeTypeName || 'Node', value: String(nd?.nodeTypeId) };
                setStepModal({ editData: nd, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}

          {/* Info always needs a next step — show Add Node regardless of level */}
          {childSteps?.length === 0 && (
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={node.nodeId}
              parentType='NODE'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              onAdded={fetchChildSteps}
            />
          )}
          </Spin>
        </div>
      )}

      {infoModal !== null && (
        <AddInfoModal
          visible={!!infoModal}
          onClose={() => setInfoModal(null)}
          onSubmit={handleInfoSubmit}
          editData={infoModal?.editData}
          onDelete={handleDeleteInfo}
        />
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleChildStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
          onDelete={handleDeleteNodeInInfo}
        />
      )}
      <NodePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        node={node}
      />
    </div>
  );
};

// ─── Info row (shows title + description) ────────────────────────────────────
const InfoRow = ({ info, onEdit, autoExpand }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (autoExpand) setOpen(true);
  }, [autoExpand]);

  return (
    <div className='agt-form-block'>
      <div className='agt-row agt-form-row'>
        <span className='agt-toggle' onClick={() => setOpen(p => !p)}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span onClick={() => setOpen(p => !p)} style={{fontSize:"18px", color:"#444", cursor: 'pointer'}}>{info.title}</span>
        <span className='agt-badge'>Info</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={onEdit} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <p style={{ fontSize: 18, color: '#555', margin: '8px 0' }} dangerouslySetInnerHTML={{ __html: info.description }} />
        </div>
      )}
    </div>
  );
};

// ─── Action step row (nodeTypeId = 3) ────────────────────────────────────────
const ActionStepRow = ({ node, nodeTypes, categoryId, onEdit, onInsertBefore, onMoveTo, loggedUserId, onNotify, onError, isTopLevel, autoExpand }) => {
  const [open, setOpen]               = useState(false);
  const [actions, setActions]         = useState([]);
  const [childSteps, setChildSteps]   = useState([]);
  const [loading, setLoading]         = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [stepModal, setStepModal]     = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const fetchAction = () => {
    Services.getActionByNodeId(node.nodeId)
      .then(res => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setActions(data?.filter(n => n.isActive !== false));
        else if (data && data.actionId && data.isActive !== false) setActions([data]);
        else setActions([]);
      })
      .catch(() => {});
  };

  const fetchChildSteps = () => {
    Services.getStepsByParentNodeId(node.nodeId, 'NODE')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    if (autoExpand) {
      setLoading(true);
      Promise.all([
        Services.getActionByNodeId(node.nodeId).then(res => {
          const data = res?.data?.data;
          if (Array.isArray(data)) setActions(data?.filter(n => n.isActive !== false));
          else if (data && data.actionId && data.isActive !== false) setActions([data]);
          else setActions([]);
        }),
        Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    setLoading(true);
    Promise.all([
      Services.getActionByNodeId(node.nodeId).then(res => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setActions(data?.filter(n => n.isActive !== false));
        else if (data && data.actionId && data.isActive !== false) setActions([data]);
        else setActions([]);
      }),
      Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
    ]).catch(() => {}).finally(() => setLoading(false));
    setOpen(p => !p);
  };

  const handleActionSubmit = async ({ actionType, title, titleBackgroundColor, subTitle, subTitleBackgroundColor, description, descriptionBackgroundColor, subDescription, subDescriptionBackgroundColor, actionConfig }) => {
    const { editData } = actionModal;
    const payload = {
      actionId:                      editData ? editData.actionId : 0,
      nodeId:                        node.nodeId,
      actionType:                    actionType || 'notification',
      title:                         title || null,
      titleBackgroundColor:          titleBackgroundColor || null,
      subTitle:                      subTitle || null,
      subTitleBackgroundColor:       subTitleBackgroundColor || null,
      description:                   description || null,
      descriptionBackgroundColor:    descriptionBackgroundColor || null,
      subDescription:                subDescription || null,
      subDescriptionBackgroundColor: subDescriptionBackgroundColor || null,
      actionConfig,
      nextNodeId:                    0,
      isActive:                      true,
      upsertedBy:                    loggedUserId,
    };
    const res = await Services.upsertAction(payload);
    if (res?.data) {
      setActionModal(null);
      fetchAction();
      setOpen(true);
      onNotify(editData ? 'Action updated.' : 'Action added.');
    }
  };

  const handleChildStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   node.nodeId,
      parentType: 'NODE',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      throw new Error(msg);
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNodeInAction = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        onNotify('Step deleted.');
      }
    } catch {
      onError('Failed to delete step.');
    }
  };

  const handleDeleteAction = async (actionId) => {
    const action = actions.find(a => a.actionId === actionId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'action', entityId: actionId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) { setActionModal(null); fetchAction(); onNotify('Action deleted.'); }
    } catch { onError('Failed to delete action.'); }
  };

  // Parse actionConfig JSON for display
  const parseConfig = (cfg) => {
    try { return typeof cfg === 'string' ? JSON.parse(cfg) : cfg; }
    catch { return {}; }
  };

  return (
    <div className='agt-step-block'>
      <div className='agt-row agt-step-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-step-icon'>{nodeTypeIcon[node?.nodeTypeName] || ''}</span>
        <span className='agt-step-name' onClick={handleToggle} style={{ cursor: 'pointer' }}>{node?.name}</span>
        <span className='agt-badge'>{node?.nodeTypeName}</span>
        <Tooltip>
          <EditOutlined className='agt-edit-icon' onClick={() => onEdit(node)} />
        </Tooltip>
        <Tooltip title="Insert Before">
          <InsertRowAboveOutlined className='agt-edit-icon' onClick={() => onInsertBefore && onInsertBefore(node)} />
        </Tooltip>
        <Tooltip title="Move To">
          <DragOutlined className='agt-edit-icon' onClick={() => onMoveTo && onMoveTo(node)} />
        </Tooltip>
        <Tooltip title="Preview">
          <EyeOutlined className='agt-edit-icon agt-preview-icon' onClick={() => setPreviewVisible(true)} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {actions?.length === 0 && !loading && <p className='agt-empty'>No action configured yet.</p>}

          {actions?.map(action => {
            const cfg = parseConfig(action.actionConfig);
            return (
              <div key={action.actionId} className='agt-form-block'>
                <div className='agt-row agt-form-row'>
                  <div style={{ flex: 1 }}>
                    {action.actionType && (
                      <div style={{ fontSize: 18, fontWeight: 300, color: '#333', marginBottom: 2 }}>
                        {action.actionType}
                      </div>
                    )}
                    <span className='' style={{fontSize: "18px", color:"#333", marginTop:"9px"}} dangerouslySetInnerHTML={{ __html: cfg.defaultMessage || '(no message)' }} />
                  </div>
                  {/* <span className='agt-badge' style={{ marginRight: 6 }}>
                    {cfg.sendEmail ? 'Email' : ''}{cfg.sendEmail && cfg.sendSMS ? ' + ' : ''}{cfg.sendSMS ? 'SMS' : ''}
                  </span> */}
                  <Tooltip title="Edit">
                    <EditOutlined
                      className='agt-edit-icon'
                      onClick={() => setActionModal({ editData: action })}
                    />
                  </Tooltip>
                </div>
              </div>
            );
          })}

          {/* Only allow one action config per node */}
          {actions?.length === 0 && (
            <div className='agt-add-btn' onClick={() => setActionModal({ editData: null })}>
              <PlusOutlined style={{ fontSize: 10 }} /> Add Action
            </div>
          )}

          {/* Child steps nested under this action node */}
          {childSteps?.map(n => (
            <StepRow
              key={n.nodeId}
              node={n}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              isTopLevel={true}
              autoExpand={autoExpand}
              onEdit={(nd) => {
                const nt = nodeTypes?.find(t => Number(t.value) === nd?.nodeTypeId)
                  || { label: nd?.nodeTypeName || 'Node', value: String(nd?.nodeTypeId) };
                setStepModal({ editData: nd, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}

          {/* Action always needs a next step — hide once one exists */}
          {childSteps?.length === 0 && (
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={node.nodeId}
              parentType='NODE'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              onAdded={fetchChildSteps}
            />
          )}
          </Spin>
        </div>
      )}

      {actionModal !== null && (
        <AddActionModal
          visible={!!actionModal}
          onClose={() => setActionModal(null)}
          onSubmit={handleActionSubmit}
          editData={actionModal?.editData}
          onDelete={handleDeleteAction}
        />
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleChildStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
          onDelete={handleDeleteNodeInAction}
        />
      )}
      <NodePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        node={node}
      />
    </div>
  );
};

// ─── Question step row (nodeTypeId = 1, 3) ────────────────────────────────────
const QuestionStepRow = ({ node, nodeTypes, categoryId, onEdit, onInsertBefore, onMoveTo, loggedUserId, onNotify, onError, isTopLevel, autoExpand }) => {
  const [open, setOpen]             = useState(false);
  const [questions, setQuestions]   = useState([]);
  const [childSteps, setChildSteps] = useState([]);
  const [loaded, setLoaded]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [qModal, setQModal]         = useState(null);
  const [stepModal, setStepModal]   = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);

  const fetchQuestions = () => {
    Services.getQuestionsByNodeId(node.nodeId)
      .then(res => setQuestions((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  const fetchChildSteps = () => {
    Services.getStepsByParentNodeId(node.nodeId, 'NODE')
      .then(res => setChildSteps((res?.data?.data || [])?.filter(n => n.isActive !== false)))
      .catch(() => {});
  };

  useEffect(() => {
    if (autoExpand && !loaded) {
      setLoading(true);
      Promise.all([
        Services.getQuestionsByNodeId(node.nodeId).then(res => setQuestions((res?.data?.data || [])?.filter(n => n.isActive !== false))),
        Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n?.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setLoaded(true);
      setOpen(true);
    }
  }, [autoExpand]);

  const handleToggle = () => {
    if (!loaded) {
      setLoading(true);
      Promise.all([
        Services.getQuestionsByNodeId(node.nodeId).then(res => setQuestions((res?.data?.data || [])?.filter(n => n.isActive !== false))),
        Services.getStepsByParentNodeId(node.nodeId, 'NODE').then(res => setChildSteps((res?.data?.data || [])?.filter(n => n?.isActive !== false)))
      ]).catch(() => {}).finally(() => setLoading(false));
      setLoaded(true);
    }
    setOpen(p => !p);
  };

  const handleQuestionSubmit = async (questionText, fields) => {
    const { editData } = qModal;
    const payload = {
      questionId:                    editData ? editData.questionId : 0,
      nodeId:                        node.nodeId,
      questionText,
      description:                   fields?.description || null,
      descriptionBackgroundColor:    fields?.descriptionBackgroundColor || null,
      title:                         fields?.title || null,
      titleBackgroundColor:          fields?.titleBackgroundColor || null,
      subTitle:                      fields?.subTitle || null,
      subTitleBackgroundColor:       fields?.subTitleBackgroundColor || null,
      subDescription:                fields?.subDescription || null,
      subDescriptionBackgroundColor: fields?.subDescriptionBackgroundColor || null,
      isActive:       true,
      questionTypeId: 1,
      upsertedBy:     loggedUserId,
    };
    const res = await Services.upsertAgentQuestion(payload);
    if (res?.data) {
      setQModal(null);
      fetchQuestions();
      onNotify(editData ? 'Question updated.' : 'Question added.');
    }
  };

  const handleChildStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   node.nodeId,
      parentType: 'NODE',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchChildSteps();
      onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleDeleteNodeInQuestion = async (nodeId) => {
    const node = childSteps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchChildSteps();
        onNotify('Step deleted.');
      }
    } catch {
      onError('Failed to delete step.');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    const question = questions?.find(q => q.questionId === questionId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'question', entityId: questionId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) { setQModal(null); fetchQuestions(); onNotify('Question deleted.'); }
    } catch { onError('Failed to delete question.'); }
  };

  return (
    <div className='agt-step-block'>
      <div className='agt-row agt-step-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-step-icon'>{nodeTypeIcon[node.nodeTypeName] || ''}</span>
        <span className='agt-step-name' onClick={handleToggle} style={{ cursor: 'pointer' }}>{node.name}</span>
        {node.nodeTypeName && <span className='agt-badge'>{node.nodeTypeName}</span>}
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={() => onEdit(node)} />
        </Tooltip>
        <Tooltip title="Insert Before">
          <InsertRowAboveOutlined className='agt-edit-icon' onClick={() => onInsertBefore && onInsertBefore(node)} />
        </Tooltip>
        <Tooltip title="Move To">
          <DragOutlined className='agt-edit-icon' onClick={() => onMoveTo && onMoveTo(node)} />
        </Tooltip>
        <Tooltip title="Preview">
          <EyeOutlined className='agt-edit-icon agt-preview-icon' onClick={() => setPreviewVisible(true)} />
        </Tooltip>
      </div>

      {open && (
        <div className='agt-indent'>
          <Spin spinning={loading} size="small">
          {questions?.length === 0 && childSteps?.length === 0 && !loading && (
            <p className='agt-empty'>No questions yet.</p>
          )}
          {questions?.map(q => (
            <QuestionRow
              key={q.questionId}
              q={q}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              parentNodeId={node.nodeId}
              autoExpand={autoExpand}
              onEdit={(qData) => setQModal({ editData: qData })}
            />
          ))}
          {questions?.length === 0 && (
            <div className='agt-add-btn' onClick={() => setQModal({ editData: null })}>
              <PlusOutlined style={{ fontSize: 10 }} /> Add Question
            </div>
          )}

          {/* Child steps nested under this node */}
          {childSteps?.map(n => (
            <StepRow
              key={n.nodeId}
              node={n}
              nodeTypes={nodeTypes}
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              autoExpand={autoExpand}
              onEdit={(nd) => {
                const nt = nodeTypes?.find(t => Number(t.value) === nd?.nodeTypeId)
                  || { label: nd?.nodeTypeName || 'Node', value: String(nd?.nodeTypeId) };
                setStepModal({ editData: nd, nodeType: nt });
              }}
              onStepAdded={fetchChildSteps}
            />
          ))}

          {!isTopLevel && (
            <AddNodeDropdown
              nodeTypes={nodeTypes}
              parentNodeId={node.nodeId}
              parentType='NODE'
              categoryId={categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}

              onError={onError}

              onAdded={fetchChildSteps}
            />
          )}
          </Spin>
        </div>
      )}

      {qModal !== null && (
        <QuestionModal
          visible={!!qModal}
          onClose={() => setQModal(null)}
          onSubmit={handleQuestionSubmit}
          editData={qModal?.editData}
          onDelete={handleDeleteQuestion}
        />
      )}
      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleChildStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
 categoryId={categoryId}
          onDelete={handleDeleteNodeInQuestion}
        />
      )}
      <NodePreviewModal
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        node={node}
      />
    </div>
  );
};

// ─── Node Preview Modal ───────────────────────────────────────────────────────
// Dynamically fetches and renders the content of a node based on its type.
// Question → questions + option cards (like the app UI screenshot)
// Form     → form name + field list
// Info     → title + HTML description
// Action   → action type + message
const NodePreviewModal = ({ visible, onClose, node }) => {
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState(null); // fetched data
  const [apiName, setApiName] = useState(null);

  useEffect(() => {
    if (!visible || !node) return;
    setContent(null);
    setApiName(null);
    setLoading(true);

    // Fetch apiName if node has apiLogId or apiLogIds
    const rawLogIds = node.apiLogIds || (node.apiLogId ? [node.apiLogId] : []);
    const logIds = rawLogIds?.map(item => typeof item === 'object' && item !== null ? (item.apiLogId || item.value) : item)?.filter(Boolean);
    if (logIds?.length > 0) {
      Services.getAgentWorkflowApiLog()
        .then(res => {
          const options = res?.data?.data || [];
          const names = logIds?.map(id => {
            const match = options?.find(o => Number(o.apiLogId || o.value) === Number(id));
            return match ? (match.apiName || match.label) : null;
          })?.filter(Boolean);
          if (names?.length > 0) setApiName(names.join(', '));
        })
        .catch(() => {});
    }

    const type = node.nodeTypeName;

    if (type === 'Question') {
      // Fetch questions for this node, then for each question fetch its options
      Services.getQuestionsByNodeId(node.nodeId)
        .then(async (res) => {
          const questions = (res?.data?.data || [])?.filter(q => q.isActive !== false);
          // For each question, fetch its options
          const questionsWithOptions = await Promise.all(
            questions?.map(async (q) => {
              try {
                const optRes = await Services.getOptionsByQuestionId(q.questionId);
                const options = (optRes?.data?.data || [])?.filter(o => o.isActive !== false);
                return { ...q, options };
              } catch {
                return { ...q, options: [] };
              }
            })
          );
          setContent({ type: 'Question', questions: questionsWithOptions });
        })
        .catch(() => setContent({ type: 'Question', questions: [] }))
        .finally(() => setLoading(false));

    } else if (type === 'Form') {
      Services.getFormByNodeId(node.nodeId)
        .then(async (res) => {
          const data = res?.data?.data;
          const forms = Array.isArray(data)
            ? data?.filter(f => f.isActive !== false)
            : data && data.formId && data.isActive !== false ? [data] : [];
          // For each form fetch its fields
          const formsWithFields = await Promise.all(
            forms?.map(async (form) => {
              try {
                const fRes = await Services.getFormFields(form.formId);
                const fields = (fRes?.data?.data || [])?.filter(f => f.isActive !== false);
                return { ...form, fields };
              } catch {
                return { ...form, fields: [] };
              }
            })
          );
          setContent({ type: 'Form', forms: formsWithFields });
        })
        .catch(() => setContent({ type: 'Form', forms: [] }))
        .finally(() => setLoading(false));

    } else if (type === 'Info') {
      Services.getInfoByNodeId(node.nodeId)
        .then((res) => {
          const data = res?.data?.data;
          const infos = Array.isArray(data)
            ? data?.filter(i => i.isActive !== false)
            : data && data.infoId && data.isActive !== false ? [data] : [];
          setContent({ type: 'Info', infos });
        })
        .catch(() => setContent({ type: 'Info', infos: [] }))
        .finally(() => setLoading(false));

    } else if (type === 'Action') {
      Services.getActionByNodeId(node.nodeId)
        .then((res) => {
          const data = res?.data?.data;
          const actions = Array.isArray(data)
            ? data?.filter(a => a.isActive !== false)
            : data && data.actionId && data.isActive !== false ? [data] : [];
          setContent({ type: 'Action', actions });
        })
        .catch(() => setContent({ type: 'Action', actions: [] }))
        .finally(() => setLoading(false));

    } else {
      setLoading(false);
      setContent({ type: 'Unknown' });
    }
  }, [visible, node]);

  const parseConfig = (cfg) => {
    try { return typeof cfg === 'string' ? JSON.parse(cfg) : (cfg || {}); }
    catch { return {}; }
  };

  const renderContent = () => {
    if (!content) return null;

    // Helper to render common styled fields (title, subTitle, description, subDescription)
    const renderStyledField = (text, bgColor, className) => {
      if (!text) return null;
      const stripped = typeof text === 'string' ? text.replace(/<[^>]*>/g, '').trim() : '';
      if (!stripped) return null;
      const style = bgColor ? { backgroundColor: bgColor } : {};
      return (
        <div className={`npv-styled-field ${className || ''}`} style={style} dangerouslySetInnerHTML={{ __html: text }} />
      );
    };

    const renderCommonFields = (item) => (
      <>
        {renderStyledField(item.title, item.titleBackgroundColor, 'npv-sf-title')}
        {renderStyledField(item.subTitle, item.subTitleBackgroundColor, 'npv-sf-subtitle')}
        {renderStyledField(item.description, item.descriptionBackgroundColor, 'npv-sf-description')}
        {renderStyledField(item.subDescription, item.subDescriptionBackgroundColor, 'npv-sf-subdescription')}
      </>
    );

    if (content.type === 'Question') {
      if (content.questions?.length === 0) {
        return <p className='npv-empty'>No questions configured for this step.</p>;
      }
      return content.questions?.map((q) => {
        // Determine which UI to show
        const hasBadges = q.options?.some(opt => opt.badge);
        const hasInfoDisplay = q.options?.some(opt => {
          if ((opt.displayType || '').toUpperCase() !== 'INFO') return false;
          let els = [];
          try { els = typeof opt.elements === 'string' ? JSON.parse(opt.elements) : (opt.elements || []); } catch { els = []; }
          return Array?.isArray(els) && els?.length > 0;
        });
        const useListView = hasBadges || hasInfoDisplay;
        const hasFormSelect = q.options?.some(opt => {
          if ((opt.displayType || '').toUpperCase() !== 'FORM') return false;
          let els = [];
          try { els = typeof opt.elements === 'string' ? JSON.parse(opt.elements) : (opt.elements || []); } catch { els = []; }
          return Array.isArray(els) && els.some(e => (e.ElementType || e.elementType || '').toUpperCase() === 'SELECT');
        });
        const hasDisplayType = !hasFormSelect && q.options?.some(opt => opt.displayType && opt.displayType !== 'BUTTON');

        return (
          <div key={q.questionId} className='npv-question-block'>
            {/* Title from API (e.g., "Select Option") - centered above options */}
            {q.title && (
              <p className='npv-select-label'>{q.title}</p>
            )}
            
            {/* SubTitle, SubDescription rendered if present */}
            {renderStyledField(q.subTitle, q.subTitleBackgroundColor, 'npv-sf-subtitle')}
            {renderStyledField(q.subDescription, q.subDescriptionBackgroundColor, 'npv-sf-subdescription')}
            
            {/* Description above options */}
            {renderStyledField(q.description, q.descriptionBackgroundColor, 'npv-sf-description')}

            {q.options?.length === 0
              ? <p className='npv-empty'>No options yet.</p>
              : useListView ? (
                /* List view with badges/INFO elements - check if ACTION displayType for card view */
                (() => {
                  const isActionCardView = q.options?.every(opt => (opt.displayType || '').toUpperCase() === 'ACTION');
                  
                  if (isActionCardView) {
                    // Card view for ACTION displayType with icons
                    return (
                      <div className='npv-action-cards'>
                        {q.options?.map((opt, idx) => {
                          // Icon mapping based on option text
                          const getIconForOption = (text) => {
                            const lower = text.toLowerCase();
                            if (lower.includes('care agency') || lower.includes('direct hire')) return '🏠';
                            if (lower.includes('insurance')) return '🛡️';
                            if (lower.includes('mortgage')) return '🏛️';
                            if (lower.includes('medicaid') || lower.includes('va')) return '📋';
                            return '📄';
                          };

                          return (
                            <div key={opt.optionId} className='npv-action-card'>
                              <div className='npv-action-card-icon'>
                                {getIconForOption(opt.optionText)}
                              </div>
                              <span className='npv-action-card-text'>{opt.optionText}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  
                  // Original numbered list view with badges
                  return (
                    <div className='npv-options-list'>
                      {q.options?.map((opt, idx) => {
                        // Parse elements if they exist
                        let parsedElements = [];
                        if (opt.elements) {
                          try {
                            parsedElements = typeof opt.elements === 'string' ? JSON.parse(opt.elements) : opt.elements;
                            if (!Array.isArray(parsedElements)) parsedElements = [];
                          } catch { parsedElements = []; }
                        }
                        const hasElements = parsedElements?.length > 0 && (opt.displayType === 'INFO' || opt.displayType === 'FORM');

                        return (
                          <div key={opt.optionId} className='npv-option-list-block'>
                            {/* Only show option item if optionText exists */}
                            {opt.optionText && (
                              <div className='npv-option-list-item'>
                                <span
                                  className='npv-option-num'
                                  style={opt.badgeColor ? { borderLeftColor: opt.badgeColor } : {}}
                                >
                                  {idx + 1}
                                </span>
                                <span className='npv-option-list-text'>{opt.optionText}</span>
                                {opt.badge && (
                                  <span
                                    className='npv-option-badge'
                                    style={{ backgroundColor: opt.badgeColor || '#720C20' }}
                                  >
                                    {opt.badge}
                                  </span>
                                )}
                              </div>
                            )}
                            {hasElements && (
                              <div className='npv-elements-section'>
                                {parsedElements?.map((el, elIdx) => {
                                  const elType = (el.ElementType || el.elementType || '').toUpperCase();
                                  const label = el.Label || el.label || '';
                                  const defaultValue = el.DefaultValue || el.defaultValue || '';

                                  if (elType === 'TEXT') {
                                    return (
                                      <p key={elIdx} className='npv-el-text'>{label || defaultValue}</p>
                                    );
                                  }

                                  if (elType === 'INFO_BOX') {
                                    return (
                                      <div key={elIdx} className='npv-el-info-box'>
                                        {label && <h4 className='npv-el-info-title'>{label}</h4>}
                                        {defaultValue && <p className='npv-el-info-desc'>{defaultValue}</p>}
                                      </div>
                                    );
                                  }

                                  // Default: just show label/value
                                  return (
                                    <div key={elIdx} className='npv-el-default'>
                                      {label && <span className='npv-el-default-label'>{label}</span>}
                                      {defaultValue && <span className='npv-el-default-value'>{defaultValue}</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              ) : hasFormSelect ? (
                /* Table style view: option text + SELECT element as actual dropdown */
                <div className='npv-form-select-table'>
                  <div className='npv-fst-header'>
                    <span className='npv-fst-header-left'>Intervention</span>
                    <span className='npv-fst-header-right'>Wish</span>
                  </div>
                  {q.options?.map((opt) => {
                    let els = [];
                    try { els = typeof opt.elements === 'string' ? JSON.parse(opt.elements) : (opt.elements || []); } catch { els = []; }
                    if (!Array.isArray(els)) els = [];
                    const selectEl = els.find(e => (e.ElementType || e.elementType || '').toUpperCase() === 'SELECT');
                    const defaultVal = selectEl ? (selectEl.DefaultValue || selectEl.defaultValue || '') : '';
                    let selectOptions = [];
                    if (selectEl) {
                      try {
                        const cfg = typeof (selectEl.Config || selectEl.config) === 'string' ? JSON.parse(selectEl.Config || selectEl.config) : (selectEl.Config || selectEl.config || {});
                        selectOptions = cfg.options || [];
                      } catch { selectOptions = []; }
                    }

                    return (
                      <div key={opt.optionId} className='npv-fst-row'>
                        <span className='npv-fst-label'>{opt.optionText}</span>
                        <select
                          className='npv-fst-select'
                          defaultValue={defaultVal}
                        >
                          {selectOptions?.map((so, soIdx) => (
                            <option key={soIdx} value={so}>{so}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              ) : hasDisplayType ? (
                /* Checkbox/Radio style view for options with displayType */
                <div className='npv-options-radio-list'>
                  {q.options?.map((opt, idx) => {
                    const isCheckbox = (opt.displayType || '').toUpperCase() === 'CHECKBOX';
                    return (
                      <div
                        key={opt.optionId}
                        className={`npv-radio-item${isCheckbox ? ' npv-radio-item-selected' : ''}`}
                      >
                        <span className={`npv-radio-circle${isCheckbox ? ' npv-radio-circle-filled' : ''}`}></span>
                        <div className='npv-radio-content'>
                          <span className='npv-radio-text'>{opt.optionText}</span>
                          {opt.description && (
                            <span className='npv-radio-desc'>{opt.description}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Original card view with images OR pill buttons if no images */
                <div>
                  {(() => {
                    // Check if any option has an image
                    const hasAnyImages = q.options?.some(opt => {
                      const optionImageMap = {
                        'illness': '/Illness.png',
                        'mental health': '/mentailhealth.svg',
                        'end of life': '/EndOfLife.png',
                        'death': '/RIP.png',
                        'hospital': '/hospital.png',
                        'care facility': '/hospital.png',
                        'home': '/home.png',
                        'others': '/others.png',
                        'other settings': '/others.png',
                        'other setting': '/others.png',
                      };
                      const imgSrc = opt?.icon || optionImageMap[opt?.optionText?.trim()?.toLowerCase()];
                      return !!imgSrc;
                    });

                    if (hasAnyImages) {
                      // Card view with images
                      return (
                        <div className='npv-options-grid'>
                          {q.options?.map(opt => {
                            const optionImageMap = {
                              'illness': '/Illness.png',
                              'mental health': '/mentailhealth.svg',
                              'end of life': '/EndOfLife.png',
                              'death': '/RIP.png',
                              'hospital': '/hospital.png',
                              'care facility': '/hospital.png',
                              'home': '/home.png',
                              'others': '/others.png',
                              'other settings': '/others.png',
                              'other setting': '/others.png',
                            };
                            const imgSrc = opt?.icon || optionImageMap[opt?.optionText?.trim()?.toLowerCase()];

                            return (
                              <div key={opt.optionId} className='npv-option-card'>
                                <div className='npv-option-icon-box'>
                                  {imgSrc ? (
                                    <img src={imgSrc} alt={opt.optionText} className='npv-option-img' />
                                  ) : (
                                    <span className='npv-option-img-placeholder' aria-label='No image'>
                                      <svg width='40' height='40' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                        <rect x='3' y='3' width='18' height='18' rx='2' stroke='#c9c9c9' strokeWidth='1.5' />
                                        <circle cx='8.5' cy='8.5' r='1.5' fill='#c9c9c9' />
                                        <path d='M21 15l-5-5L5 21' stroke='#c9c9c9' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                                <span className='npv-option-label'>{opt.optionText}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else {
                      // Pill button view (no images)
                      return (
                        <div className='npv-options-pills'>
                          {q.options?.map(opt => (
                            <div key={opt.optionId} className='npv-option-pill'>
                              {opt.optionText}
                            </div>
                          ))}
                        </div>
                      );
                    }
                  })()}
                </div>
              )
            }
          </div>
        );
      });
    }

    if (content.type === 'Form') {
      if (content.forms?.length === 0) {
        return <p className='npv-empty'>No form configured for this step.</p>;
      }

      const renderFieldByType = (f) => {
        const type = (f.fieldType || 'text').toLowerCase();

        if (type === 'checkbox') {
          return (
            <div className='npv-field-checkbox-mock'>
              <span className='npv-checkbox-box'></span>
              <span className='npv-checkbox-label'>{f.label}</span>
            </div>
          );
        }

        if (type === 'textarea') {
          return (
            <div className='npv-field-textarea-mock'>
              <span className='npv-field-input-placeholder'>Enter {f.label?.toLowerCase()}</span>
            </div>
          );
        }

        if (type === 'date') {
          return (
            <div className='npv-field-input-mock npv-field-date-mock'>
              <span className='npv-field-input-placeholder'>mm/dd/yyyy</span>
              <span className='npv-date-icon'>📅</span>
            </div>
          );
        }

        if (type === 'file') {
          return (
            <div className='npv-field-file-mock'>
              <span className='npv-file-btn'>Choose File</span>
              <span className='npv-file-text'>No file chosen</span>
            </div>
          );
        }

        if (type === 'number') {
          return (
            <div className='npv-field-input-mock'>
              <span className='npv-field-input-placeholder'>Enter {f.label?.toLowerCase()}</span>
            </div>
          );
        }

        if (type === 'email') {
          return (
            <div className='npv-field-input-mock'>
              <span className='npv-field-input-placeholder'>Enter email address</span>
            </div>
          );
        }

        if (type === 'phone') {
          return (
            <div className='npv-field-input-mock'>
              <span className='npv-field-input-placeholder'>Enter phone number</span>
            </div>
          );
        }

        if (type === 'radio') {
          return (
            <div className='npv-field-radio-mock'>
              <input type="radio" name={`radio-group-${f.formId || 'form'}`} style={{ accentColor: '#720C20', width: 18, height: 18 }} />
              <span className='npv-radio-label'>{f.label}</span>
            </div>
          );
        }

        if (type === 'select') {
          const options = (() => {
            try {
              if (Array.isArray(f.fieldOptions)) return f.fieldOptions;
              if (typeof f.fieldOptions === 'string') return JSON.parse(f.fieldOptions);
              return [];
            } catch { return []; }
          })();
          return (
            <div className='npv-field-select-mock'>
              <select className='npv-select-input' style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: 4, 
                border: '1px solid #d9d9d9',
                backgroundColor: '#fff',
                fontSize: 14,
                color: '#333',
                cursor: 'pointer',
                outline: 'none'
              }}>
                <option value="">Select {f.label?.toLowerCase()}</option>
                {options?.map((opt, idx) => (
                  <option key={idx} value={opt.value}>{opt.text}</option>
                ))}
              </select>
            </div>
          );
        }

        // Default: text
        return (
          <div className='npv-field-input-mock'>
            <span className='npv-field-input-placeholder'>Enter {f.label?.toLowerCase()}</span>
          </div>
        );
      };

      return content.forms?.map(form => (
        <div key={form.formId} className='npv-form-block'>
          {renderCommonFields(form)}
          {form.fields?.length === 0
            ? <p className='npv-empty' style={{ marginTop: 12 }}>No fields configured.</p>
            : (
              <div className='npv-fields-list'>
                {form.fields?.map(f => {
                  const type = (f.fieldType || 'text').toLowerCase();
                  return (
                    <div key={f.fieldId} className='npv-field-row'>
                      {type !== 'checkbox' && type !== 'radio' && <span className='npv-field-label'>{f.label}</span>}
                      {renderFieldByType(f)}
                    </div>
                  );
                })}
              </div>
            )
          }
        </div>
      ));
    }

    if (content.type === 'Info') {
      if (content.infos?.length === 0) {
        return <p className='npv-empty'>No info content configured for this step.</p>;
      }
      return content.infos?.map(info => (
        <div key={info.infoId} className='npv-info-block'>
          {/* {renderStyledField(info.title, info.titleBackgroundColor, 'npv-sf-title')} */}
          {renderStyledField(info.subTitle, info.subTitleBackgroundColor, 'npv-sf-subtitle')}
          {apiName && (
            <div className='npv-api-name-box'>
              <span className='npv-api-name-label'>API:</span>
              <span className='npv-api-name-text'>{apiName}</span>
            </div>
          )}
          {renderStyledField(info.description, info.descriptionBackgroundColor, 'npv-sf-description')}
          {renderStyledField(info.subDescription, info.subDescriptionBackgroundColor, 'npv-sf-subdescription')}
        </div>
      ));
    }

    if (content.type === 'Action') {
      if (content.actions?.length === 0) {
        return <p className='npv-empty'>No action configured for this step.</p>;
      }
      return content.actions?.map(action => {
        const cfg = parseConfig(action.actionConfig);
        return (
          <div key={action.actionId} className='npv-action-block'>
            {renderStyledField(action.title, action.titleBackgroundColor, 'npv-sf-title')}
            {renderStyledField(action.subTitle, action.subTitleBackgroundColor, 'npv-sf-subtitle')}
            {renderStyledField(action.description, action.descriptionBackgroundColor, 'npv-sf-description')}
            {cfg.defaultMessage && cfg.defaultMessage.replace(/<[^>]*>/g, '').trim() && (
              <div
                className='npv-action-message'
                dangerouslySetInnerHTML={{ __html: cfg.defaultMessage }}
              />
            )}
            {renderStyledField(action.subDescription, action.subDescriptionBackgroundColor, 'npv-sf-subdescription')}
          </div>
        );
      });
    }

    return <p className='npv-empty'>No preview available.</p>;
  };

  // Build dynamic header title — node name + inner content title
  const getHeaderTitle = () => {
    const base = node?.name || '';
    if (!content) return base;

    if (content.type === 'Question' && content.questions?.length > 0) {
      const questionText = content.questions[0].questionText || '';
      return `${base} ${questionText}`;
    }

    if (content.type === 'Form' && content.forms?.length > 0) {
      const formName = content.forms[0]?.formName || '';
      return formName ? `${base} ${formName}` : base;
    }

    if (content.type === 'Info' && content.infos?.length > 0) {
      const infoTitle = content.infos[0]?.title || '';
      return infoTitle ? `${base} ${infoTitle}` : base;
    }

    if (content.type === 'Action' && content.actions?.length > 0) {
      const actionType = content.actions[0].actionType || '';
      return actionType ? `${base} ${actionType}` : base;
    }

    return base;
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      // width={620}
      className='npv-modal ag-modal'
      // bodyStyle={{ padding: 0 }}
      closable={false}
      width={750}
      style={{ top: 20 }}
      bodyStyle={{ padding: '10px', maxHeight: 'calc(100vh - 80px)', overflowY: 'auto', borderRadius: '8px' }}
    >
      <div className='npv-phone-frame'>
        {/* Close button — top-right circle */}
        <div className='npv-close-btn' onClick={onClose}>✕</div>

        {/* Header: node name + inner content title combined */}
        <div className='npv-modal-header'>
          <span className='npv-modal-title'>{getHeaderTitle()}</span>
          <span className='npv-modal-badge'>{node?.nodeTypeName}</span>
        </div>

        {/* Scrollable content */}
        <div className='npv-modal-body'>
          <Spin spinning={loading} size='large'>
            {renderContent()}
          </Spin>
          
          {/* Center buttons (when isFooter is false) */}
          {(() => {
            try {
              const btns = typeof node?.buttons === 'string' ? JSON.parse(node.buttons) : (node?.buttons || []);
              const centerButtons = btns?.filter(b => (b.IsActive !== false && b.isActive !== false) && (b.IsFooter === false || b.isFooter === false));
              if (centerButtons?.length > 0) {
                return (
                  <div className='npv-center-buttons'>
                    {centerButtons?.map((btn, idx) => {
                      const btnText = btn.ButtonText || btn.buttonText || '';
                      const isSendLater = btnText.toLowerCase().includes('send later');
                      return (
                        <div 
                          key={btn.ButtonId || btn.buttonId || idx} 
                          className={`npv-center-btn${isSendLater ? ' npv-center-btn-yellow' : ''}`}
                        >
                          {btnText}
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return null;
            } catch { return null; }
          })()}
        </div>

        {/* Footer — Back button left, Send Later center, other buttons right */}
        <div className='npv-modal-footer'>
          <div className='npv-back-btn' onClick={onClose}>Back</div>
          
          {/* Center section for "Send Later" button */}
          <div className='npv-footer-center'>
            {(() => {
              try {
                const btns = typeof node?.buttons === 'string' ? JSON.parse(node.buttons) : (node?.buttons || []);
                const footerButtons = btns?.filter(b => (b.IsActive !== false && b.isActive !== false) && (b.IsFooter === true || b.isFooter === true));
                const sendLaterBtn = footerButtons?.find(b => {
                  const btnText = (b.ButtonText || b.buttonText || '').toLowerCase();
                  return btnText.includes('send later');
                });
                
                if (sendLaterBtn) {
                  return (
                    <div className='npv-node-btn npv-node-btn-yellow'>
                      {sendLaterBtn.ButtonText || sendLaterBtn.buttonText}
                    </div>
                  );
                }
                return null;
              } catch { return null; }
            })()}
          </div>
          
          {/* Right section for other buttons (not "Send Later") */}
          <div className='npv-footer-buttons'>
            {(() => {
              try {
                const btns = typeof node?.buttons === 'string' ? JSON.parse(node.buttons) : (node?.buttons || []);
                const footerButtons = btns?.filter(b => (b.IsActive !== false && b.isActive !== false) && (b.IsFooter === true || b.isFooter === true));
                const otherButtons = footerButtons?.filter(b => {
                  const btnText = (b.ButtonText || b.buttonText || '').toLowerCase();
                  return !btnText.includes('send later');
                });
                
                return otherButtons?.map((btn, idx) => (
                  <div key={btn.ButtonId || btn.buttonId || idx} className='npv-node-btn'>
                    {btn.ButtonText || btn.buttonText}
                  </div>
                ));
              } catch { return null; }
            })()}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// ─── Inline Question Modal (reuses AddNodeModal style) ────────────────────────
const QuestionModal = ({ visible, onClose, onSubmit, editData, onDelete }) => {
  const { Modal, Input, Button } = require('antd');
  const [text, setText]     = useState('');
  const [description, setDescription] = useState('');
  const [descriptionBackgroundColor, setDescriptionBackgroundColor] = useState('');
  const [title, setTitle] = useState('');
  const [titleBackgroundColor, setTitleBackgroundColor] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [subTitleBackgroundColor, setSubTitleBackgroundColor] = useState('');
  const [subDescription, setSubDescription] = useState('');
  const [subDescriptionBackgroundColor, setSubDescriptionBackgroundColor] = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const isEdit = !!editData;

  useEffect(() => {
    if (visible) {
      setText(editData ? editData.questionText : '');
      setDescription(editData ? (editData.description || '') : '');
      setDescriptionBackgroundColor(editData ? (editData.descriptionBackgroundColor || '') : '');
      setTitle(editData ? (editData.title || '') : '');
      setTitleBackgroundColor(editData ? (editData.titleBackgroundColor || '') : '');
      setSubTitle(editData ? (editData.subTitle || '') : '');
      setSubTitleBackgroundColor(editData ? (editData.subTitleBackgroundColor || '') : '');
      setSubDescription(editData ? (editData.subDescription || '') : '');
      setSubDescriptionBackgroundColor(editData ? (editData.subDescriptionBackgroundColor || '') : '');
      setError('');
    }
  }, [visible, editData]);

  const handleSubmit = async () => {
    if (!text.trim()) { setError('Question text is required.'); return; }
    setError('');
    setLoading(true);
    try {
      await onSubmit(text.trim(), {
        description: description.trim() || null,
        descriptionBackgroundColor: descriptionBackgroundColor.trim() || null,
        title: title.trim() || null,
        titleBackgroundColor: titleBackgroundColor.trim() || null,
        subTitle: subTitle.trim() || null,
        subTitleBackgroundColor: subTitleBackgroundColor.trim() || null,
        subDescription: subDescription.trim() || null,
        subDescriptionBackgroundColor: subDescriptionBackgroundColor.trim() || null,
      });
      setText(''); setDescription(''); setTitle(''); setSubTitle(''); setSubDescription('');
    }
    catch { setError('Failed to save.'); }
    finally { setLoading(false); }
  };

  return (
    <Modal
      title={<span style={{ fontSize: 16, color: '#474747', fontWeight: "bold" }}>{editData ? 'Edit Question' : 'Add Question'}</span>}
      visible={visible} onCancel={onClose} footer={null} centered destroyOnClose className="ag-modal" width={620}
      bodyStyle={{ padding: '20px 28px 28px', borderTop:"1px solid #bfbfbf", maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {/* Question Text */}
      <div style={{ marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>Question Text <span style={{ color: '#720C20' }}>*</span></label>
      </div>
      <Input
        placeholder="Type question here ?"
        value={text}
        onChange={e => { setText(e.target.value); if (error) setError(''); }}
        style={{ borderRadius: 2, borderColor: error ? '#ff4d4f' : '#720C20', height: 40 }}
      />
      {error && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{error}</p>}

      {/* Title & Background Color */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 13, color: '#333' }}>Title <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          <Input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          <Input placeholder="#ffffff" value={titleBackgroundColor} onChange={e => setTitleBackgroundColor(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Sub Title & Background Color */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 13, color: '#333' }}>Sub Title <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          <Input placeholder="Sub title" value={subTitle} onChange={e => setSubTitle(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          <Input placeholder="#ffffff" value={subTitleBackgroundColor} onChange={e => setSubTitleBackgroundColor(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Description & Background Color */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 13, color: '#333' }}>Description <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <Input.TextArea placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ flex: 2, borderRadius: 2 }} />
          <Input placeholder="#ffffff" value={descriptionBackgroundColor} onChange={e => setDescriptionBackgroundColor(e.target.value)} style={{ flex: 1, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Sub Description & Background Color */}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 13, color: '#333' }}>Sub Description <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <div style={{ flex: 2, border: '1px solid #d9d9d9', borderRadius: 2, overflow: 'hidden' }}>
            <JoditEditor
              value={subDescription}
              config={joditConfigSmall}
              onBlur={val => setSubDescription(val)}
            />
          </div>
          <Input placeholder="#ffffff" value={subDescriptionBackgroundColor} onChange={e => setSubDescriptionBackgroundColor(e.target.value)} style={{ flex: 1, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end', alignItems: 'center', gap: 10, marginTop: 24 }}>
        {isEdit && onDelete && (
          <Button danger icon={<DeleteOutlined color='rgb(114, 12, 32)' />} onClick={() => setConfirmVisible(true)} disabled={loading} style={{ borderRadius: 2, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4, borderColor:"rgb(114, 12,32)", color:"rgb(114, 12, 32)" }}>Delete</Button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
        <Button onClick={onClose} disabled={loading} style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 2, fontWeight: 500 }}>Cancel</Button>
        <Button loading={loading} onClick={handleSubmit} style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', borderRadius: 2, fontWeight: 500 }}>
          {editData ? 'Update Question' : 'Add Question'}
        </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={420}
        zIndex={1200}
        closable
        className="ag-modal"
        bodyStyle={{ padding: '28px 28px 20px' }}
      >
        <p style={{ fontSize: 15, color: '#333', marginBottom: 20, textAlign: 'center' }}>
          Are you sure you want to delete <strong>{editData?.questionText}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={() => setConfirmVisible(false)} style={{ minWidth: 100, height: 36, borderRadius: 4, borderColor: '#720C20', color: '#720C20' }}>Cancel</Button>
          <Button
            onClick={() => { setConfirmVisible(false); onDelete(editData.questionId); }}
            style={{ minWidth: 100, height: 36, borderRadius: 4, backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff' }}
          >Delete</Button>
        </div>
      </Modal>
    </Modal>
  );
};

// ─── Preview Screen (fullscreen overlay with same tree structure + edit) ──────
const PreviewScreen = ({ visible, onClose, cat, nodeTypes, loggedUserId, onNotify, onError }) => {
  const [steps, setSteps]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [stepModal, setStepModal] = useState(null);

  const fetchSteps = () => {
    setLoading(true);
    Services.getStepsByCategoryId(cat?.categoryId)
      .then(res => setSteps((res?.data?.data || [])?.filter(n => n?.isActive !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (visible && cat?.categoryId) {
      fetchSteps();
    }
  }, [visible, cat?.categoryId]);

  const handleDeleteNode = async (nodeId) => {
    const node = steps.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });
      if (res?.data) {
        setStepModal(null);
        fetchSteps();
        if (onNotify) onNotify('Step deleted.');
      }
    } catch {
      if (onError) onError('Failed to delete step.');
    }
  };

  const handleStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      parentId:   cat?.categoryId,
      parentType: 'CATEGORY',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: cat?.categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchSteps();
      if (onNotify) onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  if (!visible || !cat) return null;

  return (
    <div className='agt-preview-screen'>
      {/* Header bar */}
      <div className='agt-preview-header'>
        <span className='agt-preview-title'>{cat.categoryName} - Preview</span>
        <button className='agt-preview-close-btn' onClick={onClose}>Close</button>
      </div>

      {/* Content area */}
      <div className='agt-preview-body'>
        <Spin spinning={loading} size="large">
          <div className='agt-tree agt-preview-tree'>
            {steps?.length === 0 && !loading && <p className='agt-empty'>No steps yet.</p>}
            {steps?.map(node => (
              <StepRow
                key={node.nodeId}
                node={node}
                nodeTypes={nodeTypes}
                categoryId={cat.categoryId}
                loggedUserId={loggedUserId}
                onNotify={onNotify}
                onError={onError}
                isTopLevel={true}
                autoExpand={true}
                onEdit={(n) => {
                  const nt = nodeTypes?.find(t => Number(t.value) === n?.nodeTypeId)
                    || { label: n?.nodeTypeName || 'Node', value: String(n?.nodeTypeId) };
                  setStepModal({ editData: n, nodeType: nt });
                }}
                onStepAdded={fetchSteps}
              />
            ))}
          </div>
        </Spin>

        {stepModal !== null && (
          <StepModal
            visible={!!stepModal}
            onClose={() => setStepModal(null)}
            onSubmit={handleStepSubmit}
            nodeType={stepModal?.nodeType}
            editData={stepModal?.editData}
            nodeTypes={nodeTypes}
            categoryId={cat?.categoryId}
            onDelete={handleDeleteNode}
          />
        )}
      </div>
    </div>
  );
};

// ─── Category block ───────────────────────────────────────────────────────────
const CategoryBlock = ({ cat, nodeTypes, onEditCat, loggedUserId, onNotify, onError }) => {
  const [open, setOpen]     = useState(false);
  const [steps, setSteps]   = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [stepModal, setStepModal] = useState(null); // null | { editData, nodeType }
  const [previewVisible, setPreviewVisible] = useState(false);
  const [visibility, setVisibility] = useState(cat?.isDisplay === false ? 'hide' : 'show');
  const [visDropOpen, setVisDropOpen] = useState(false);

  const fetchSteps = () => {
    setLoading(true);
    Services.getStepsByCategoryId(cat.categoryId)
      .then(res => setSteps((res?.data?.data || [])?.filter(n => n?.isActive !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Listen for tree refresh events (triggered by Move To)
  const [treeKey, setTreeKey] = useState(0);
  useEffect(() => {
    const handleRefresh = () => {
      if (loaded) {
        fetchSteps();
        setTreeKey(k => k + 1);
      }
    };
    window.addEventListener('agentTreeRefresh', handleRefresh);
    return () => window.removeEventListener('agentTreeRefresh', handleRefresh);
  }, [loaded]);

  const handleToggle = () => {
    if (!loaded) { fetchSteps(); setLoaded(true); }
    setOpen(p => !p);
  };

  const handleDeleteNode = async (nodeId) => {
    const node = steps?.find(n => n.nodeId === nodeId);
    try {
      const res = await Services.deleteWorkflowEntity({ entityType: 'node', entityId: nodeId, deletedBy: loggedUserId, userId: loggedUserId });

      if (res?.data) {
        setStepModal(null);
        fetchSteps();
        onNotify('Step deleted.');
      }
    } catch {
      onError('Failed to delete step.');
    }
  };

  const handleStepSubmit = async (name, chosenType, buttons, apiLogIds, paymentResource, header, sameScreenId) => {
    const { editData } = stepModal;
    const payload = {
      nodeId:     editData ? editData.nodeId : 0,
      name,
      // parentId: 0,
      parentId:   cat?.categoryId,
      parentType: 'CATEGORY',
      orderNo:    0,
      nodeTypeId: Number(chosenType.value),
      categoryId: cat?.categoryId,
      isActive:   true,
      buttons:    buttons || [],
      apiLogIds:  apiLogIds || [],
      paymentResource: paymentResource || null,
      header:          header || null,
      sameScreenId:    sameScreenId || null,
      upsertedBy: loggedUserId,
    };
    const res = await Services.upsertAgentStep(payload);
    if (res?.data?.succeeded === false || res?.data?.Succeeded === false) {
      const msg = res?.data?.messages?.[0] || res?.data?.Messages?.[0] || 'Failed to add step.';
      if (onError) onError(msg);
      return;
    }
    if (res?.data) {
      setStepModal(null);
      fetchSteps();
      onNotify(editData ? 'Step updated.' : 'Step added.');
    }
  };

  const handleVisibilityChange = async (newVisibility) => {
    const isDisplay = newVisibility === 'show';
    try {
      const res = await Services.updateAgentCategoryDisplay({
        categoryId: cat?.categoryId,
        isDisplay,
        updatedBy: loggedUserId,
      });
      if (res?.data) {
        setVisibility(newVisibility);
        onNotify(`Category ${isDisplay ? 'shown' : 'hidden'} successfully.`);
      }
    } catch {
      onError('Failed to update category display.');
    }
  };

  return (
    <div className='agt-category-block'>
      {/* Category row */}
      <div className='agt-row agt-cat-row'>
        <span className='agt-toggle' onClick={handleToggle}>
          {open ? <MinusOutlined /> : <PlusOutlined />}
        </span>
        <span className='agt-cat-name' onClick={handleToggle} style={{ cursor: 'pointer' }}>{cat.categoryName}</span>
        <Tooltip title="Edit">
          <EditOutlined className='agt-edit-icon' onClick={() => onEditCat(cat)} />
        </Tooltip>
        <Tooltip title="Print tree">
          <PrinterOutlined
            className='agt-edit-icon'
            style={{ marginLeft: 6 }}
            onClick={() => handlePrintTree(cat.categoryId, cat.categoryName, setPdfLoading, onError)}
          />
        </Tooltip>
        <Tooltip title="Download PDF">
          <FilePdfOutlined
            className='agt-edit-icon'
            style={{ marginLeft: 6 }}
            onClick={() => handleDownloadPdf(cat.categoryId, cat.categoryName, setPdfLoading, onError)}
          />
        </Tooltip>
        {/* <Tooltip title="Preview">
          <FullscreenOutlined
            className='agt-edit-icon'
            style={{ marginLeft: 6 }}
            onClick={() => setPreviewVisible(true)}
          />
        </Tooltip> */}
        {pdfLoading && <Spin size="small" style={{ marginLeft: 8 }} />}

        {/* Custom Show/Hide select box */}
        <div className='agt-vis-select' onClick={() => setVisDropOpen(p => !p)}>
          <span className='agt-vis-select-label'>{visibility === 'show' ? 'Show' : 'Hide'}</span>
          <span className='agt-vis-select-arrow'>{visDropOpen ? '▲' : '▼'}</span>
          {visDropOpen && (
            <div className='agt-vis-dropdown'>
              <div
                className={`agt-vis-dropdown-item${visibility === 'show' ? ' agt-vis-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleVisibilityChange('show'); setVisDropOpen(false); }}
              >
                Show
              </div>
              <div
                className={`agt-vis-dropdown-item${visibility === 'hide' ? ' agt-vis-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleVisibilityChange('hide'); setVisDropOpen(false); }}
              >
                Hide
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className='agt-indent' key={treeKey}>
          <Spin spinning={loading} size="small">
          {/* Add Node dropdown — top-level under category */}
          <AddNodeDropdown
            nodeTypes={nodeTypes}
            // parentNodeId={0}
            parentNodeId={cat.categoryId}
            parentType='CATEGORY'
            categoryId={cat.categoryId}
            loggedUserId={loggedUserId}
            onNotify={onNotify}

            onError={onError}

            onAdded={fetchSteps}
          />

          {steps?.length === 0 && !loading && <p className='agt-empty'>No steps yet.</p>}
          {steps?.map(node => (
            <StepRow
              key={node.nodeId}
              node={node}
              nodeTypes={nodeTypes}
              categoryId={cat.categoryId}
              loggedUserId={loggedUserId}
              onNotify={onNotify}
              onError={onError}
              isTopLevel={true}
              onEdit={(n) => {
                const nt = nodeTypes?.find(t => Number(t.value) === n?.nodeTypeId)
                  || { label: n?.nodeTypeName || 'Node', value: String(n?.nodeTypeId) };
                setStepModal({ editData: n, nodeType: nt });
              }}
              onStepAdded={fetchSteps}
            />
          ))}
          </Spin>
        </div>
      )}

      {stepModal !== null && (
        <StepModal
          visible={!!stepModal}
          onClose={() => setStepModal(null)}
          onSubmit={handleStepSubmit}
          nodeType={stepModal?.nodeType}
          editData={stepModal?.editData}
          nodeTypes={nodeTypes}
          categoryId={cat?.categoryId}
          onDelete={handleDeleteNode}
        />
      )}

      <PreviewScreen
        visible={previewVisible}
        onClose={() => setPreviewVisible(false)}
        cat={cat}
        nodeTypes={nodeTypes}
        loggedUserId={loggedUserId}
        onNotify={onNotify}
        onError={onError}
      />
    </div>
  );
};

// ─── Step Modal ───────────────────────────────────────────────────────────────
const StepModal = ({ visible, onClose, onSubmit, nodeType, editData, nodeTypes, onDelete, categoryId }) => {
  const { Modal, Input, Button, Select } = require('antd');
  const { Option } = Select;
  const [text, setText]           = useState('');
  const [selectedType, setSelectedType] = useState(undefined);
  const [buttons, setButtons]     = useState([]);
  const [btnText, setBtnText]     = useState('');
  const [btnLink, setBtnLink]     = useState('');
  const [btnIsFooter, setBtnIsFooter] = useState(false);
  const [btnProSerDescId, setBtnProSerDescId] = useState(undefined);
  const [btnProTypeId, setBtnProTypeId] = useState(undefined);
  const [proSerDescOptions, setProSerDescOptions] = useState([]);
  const [proTypeOptions, setProTypeOptions] = useState([]);
  const [proTypeLoading, setProTypeLoading] = useState(false);
  const [editingBtnIdx, setEditingBtnIdx] = useState(null);
  const [error, setError]         = useState({});
  const [loading, setLoading]     = useState(false);
  const [apiOptions, setApiOptions]     = useState([]);
  const [apiOptionsLoading, setApiOptionsLoading] = useState(false);
  const [selectedApiLogIds, setSelectedApiLogIds]   = useState([]);
  const [nextNodeOptions, setNextNodeOptions] = useState([]);
  const [paymentResource, setPaymentResource] = useState('');
  const [sameScreenId, setSameScreenId] = useState(undefined);
  const [screenTemplates, setScreenTemplates] = useState([]);
  const [screenTemplatesLoading, setScreenTemplatesLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  // Header state
  const [headerText1, setHeaderText1]             = useState('');
  const [headerText2, setHeaderText2]             = useState('');
  const [headerText3, setHeaderText3]             = useState('');
  const [headerBtnText, setHeaderBtnText]         = useState('');
  const [headerBtnType, setHeaderBtnType]         = useState('');
  const [headerBtnAction, setHeaderBtnAction]     = useState('');
  const [headerBtnIcon, setHeaderBtnIcon]         = useState('');
  const [headerBtnNavigateTo, setHeaderBtnNavigateTo] = useState('');

  useEffect(() => {
    if (visible) {
      setText(editData ? editData.name : '');
      setSelectedType(editData ? String(editData.nodeTypeId) : nodeType?.value);
      const parsedBtns = (() => {
        try {
          const raw = editData?.buttons;
          if (!raw) return [];
          const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
          console.log('Raw buttons from API:', arr);
          // Normalize button properties to handle both PascalCase and camelCase
          const filtered = Array.isArray(arr) ? arr?.filter(b => b.IsActive !== false && b.isActive !== false) : [];
          const normalized = filtered.map(b => ({
            ...b,
            buttonId: b.ButtonId || b.buttonId || 0,
            buttonText: b.ButtonText || b.buttonText || '',
            buttonType: b.ButtonType || b.buttonType || null,
            nextNodeId: b.NextNodeId || b.nextNodeId || null,
            orderNo: b.OrderNo || b.orderNo || null,
            isActive: b.isActive !== false && b.IsActive !== false,
            link: b.Link || b.link || null,
            isFooter: !!(b.IsFooter || b.isFooter),
            proTypeId: b.proTypeId || b.ProTypeId || null,
          }));
          console.log('Normalized buttons:', normalized);
          return normalized;
        } catch (err) { 
          console.error('Error parsing buttons:', err);
          return []; 
        }
      })();
      setButtons(parsedBtns);
      setBtnText('');
      setBtnLink('');
      setBtnIsFooter(false);
      setEditingBtnIdx(null);
      setError({});
      setSelectedApiLogIds((() => {
        if (editData?.apiLogIds && Array.isArray(editData.apiLogIds)) {
          return editData.apiLogIds.map(item => {
            if (typeof item === 'object' && item !== null) return String(item.apiLogId || item.value);
            return String(item);
          });
        }
        if (editData?.apiLogId) return [String(editData.apiLogId)];
        return [];
      })());
      fetchApiOptions();
      fetchScreenTemplates();
      fetchProfessionalSecDesc();
      setPaymentResource(editData?.paymentResource || '');
      setSameScreenId(editData?.sameScreenId || undefined);
      // Pre-fill header fields from editData
      // API returns header as a JSON string with PascalCase keys
      let h = {};
      if (editData?.header) {
        try {
          h = typeof editData.header === 'string' ? JSON.parse(editData.header) : editData.header;
        } catch { h = {}; }
      }
      setHeaderText1(h.Text1 || h.text1 || '');
      setHeaderText2(h.Text2 || h.text2 || '');
      setHeaderText3(h.Text3 || h.text3 || '');
      const hBtn = h.Button || h.button || {};
      setHeaderBtnText(hBtn.ButtonText || hBtn.buttonText || '');
      setHeaderBtnType(hBtn.ButtonType || hBtn.buttonType || '');
      setHeaderBtnAction(hBtn.ButtonAction || hBtn.buttonAction || '');
      setHeaderBtnIcon(hBtn.ButtonIcon || hBtn.buttonIcon || '');
      setHeaderBtnNavigateTo(hBtn.NavigateTo || hBtn.navigateTo || '');
      if (categoryId) fetchNextNodeOptions();
    }
  }, [visible, editData, nodeType]);

  const fetchNextNodeOptions = async () => {
    try {
      const res = await Services.getAllStepsByCategoryId(categoryId);
      const data = (res?.data?.data || [])?.filter(n => n.isActive !== false && n.nodeId !== editData?.nodeId);
      // Simply use the step data without fetching additional details
      setNextNodeOptions(data);
    } catch {
      setNextNodeOptions([]);
    }
  };

  const fetchApiOptions = async () => {
    setApiOptionsLoading(true);
    try {
      const res = await Services.getAgentWorkflowApiLog();
      const data = res?.data?.data || [];
      setApiOptions(Array.isArray(data) ? data : []);
    } catch {
      setApiOptions([]);
    } finally {
      setApiOptionsLoading(false);
    }
  };

  const fetchScreenTemplates = async () => {
    setScreenTemplatesLoading(true);
    try {
      const res = await Services.getAgentScreenTemplates();
      const data = res?.data?.data || [];
      setScreenTemplates(Array.isArray(data) ? data?.filter(t => t.isActive !== false) : []);
    } catch {
      setScreenTemplates([]);
    } finally {
      setScreenTemplatesLoading(false);
    }
  };

  const fetchProfessionalSecDesc = async () => {
    try {
      const res = await Services.getProfessionalSecDesc();
      const data = res?.data?.data || [];
      setProSerDescOptions(Array.isArray(data) ? data : []);
    } catch {
      setProSerDescOptions([]);
    }
  };

  const fetchProfessionalTypes = async (proSerDescId) => {
    if (!proSerDescId) {
      setProTypeOptions([]);
      return;
    }
    setProTypeLoading(true);
    try {
      const res = await Services.getProfessionalSerType(proSerDescId);
      const data = res?.data?.data || [];
      setProTypeOptions(Array.isArray(data) ? data : []);
    } catch {
      setProTypeOptions([]);
    } finally {
      setProTypeLoading(false);
    }
  };

  const findServiceCategoryForProType = async (proTypeId) => {
    // Try to find which service category contains this professional type
    // We'll iterate through all service categories and check their professional types
    try {
      for (const category of proSerDescOptions) {
        const res = await Services.getProfessionalSerType(category.value);
        const proTypes = res?.data?.data || [];
        const found = proTypes.find(pt => Number(pt.value) === Number(proTypeId));
        if (found) {
          setBtnProSerDescId(category.value);
          setProTypeOptions(proTypes);
          return;
        }
      }
    } catch (err) {
      console.error('Error finding service category for professional type:', err);
    }
  };

  const handleAddButton = () => {
    if (!btnText.trim()) return;
    
    if (editingBtnIdx !== null) {
      // Update existing button
      setButtons(prev => prev?.map((b, i) => 
        i === editingBtnIdx 
          ? { 
              ...b,
              buttonText: btnText.trim(),
              ButtonText: btnText.trim(),
              link: btnLink.trim() || null,
              Link: btnLink.trim() || null,
              isFooter: btnIsFooter,
              IsFooter: btnIsFooter,
              proTypeId: btnProTypeId || null,
            }
          : b
      ));
      setEditingBtnIdx(null);
    } else {
      // Add new button
      setButtons(prev => [...prev, { 
        buttonId: 0, 
        buttonText: btnText.trim(), 
        buttonType: null, 
        nextNodeId: null, 
        orderNo: null, 
        isActive: true,
        link: btnLink.trim() || null,
        isFooter: btnIsFooter,
        proTypeId: btnProTypeId || null,
      }]);
    }
    
    setBtnText('');
    setBtnLink('');
    setBtnIsFooter(false);
    setBtnProSerDescId(undefined);
    setBtnProTypeId(undefined);
    setProTypeOptions([]);
  };

  const handleEditButton = (idx) => {
    const btn = buttons[idx];
    setBtnText(btn.ButtonText || btn.buttonText || '');
    setBtnLink(btn.Link || btn.link || '');
    setBtnIsFooter(!!(btn.IsFooter || btn.isFooter));
    
    const existingProTypeId = btn.proTypeId || btn.ProTypeId;
    setBtnProTypeId(existingProTypeId || undefined);
    
    // If button has proTypeId, try to find which service category it belongs to
    if (existingProTypeId && proSerDescOptions?.length > 0) {
      findServiceCategoryForProType(existingProTypeId);
    }
    
    setEditingBtnIdx(idx);
  };

  const handleCancelEditButton = () => {
    setBtnText('');
    setBtnLink('');
    setBtnIsFooter(false);
    setBtnProSerDescId(undefined);
    setBtnProTypeId(undefined);
    setProTypeOptions([]);
    setEditingBtnIdx(null);
  };

  const handleRemoveButton = (idx) => {
    if (editingBtnIdx === idx) {
      handleCancelEditButton();
    }
    setButtons(prev => {
      const btn = prev[idx];
      // If it's an existing button from API (has ButtonId or buttonId > 0), mark isActive false
      const existingId = btn?.ButtonId || btn?.buttonId;
      if (existingId && existingId > 0) {
        return prev?.map((b, i) => i === idx ? { ...b, isActive: false, IsActive: false } : b);
      }
      // New button — just remove from list
      return prev?.filter((_, i) => i !== idx);
    });
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!text.trim())    errs.name = 'Name is required.';
    if (!selectedType)   errs.type = 'Node type is required.';
    if (Object.keys(errs)?.length > 0) { setError(errs); return; }
    setError({});
    setLoading(true);
    try {
      const chosenType = nodeTypes?.find(nt => nt.value === selectedType)
        || { label: nodeType?.label, value: selectedType };
      // Auto-include any text still in the button input
      let finalButtons = [...buttons];
      if (btnText.trim()) {
        if (editingBtnIdx !== null) {
          // Update existing button in the list
          finalButtons = finalButtons?.map((b, i) => 
            i === editingBtnIdx 
              ? { 
                  ...b,
                  buttonText: btnText.trim(),
                  ButtonText: btnText.trim(),
                  link: btnLink.trim() || null,
                  Link: btnLink.trim() || null,
                  isFooter: btnIsFooter,
                  IsFooter: btnIsFooter,
                }
              : b
          );
        } else {
          // Add new button
          finalButtons.push({ 
            buttonId: 0, 
            buttonText: btnText.trim(), 
            buttonType: null, 
            nextNodeId: null, 
            orderNo: null, 
            isActive: true,
            link: btnLink.trim() || null,
            isFooter: btnIsFooter,
          });
        }
      }
      // Normalize button shape for API (handle PascalCase from API response)
      finalButtons = finalButtons?.map(b => ({
        buttonId: b.ButtonId || b.buttonId || 0,
        buttonText: b.ButtonText || b.buttonText || '',
        buttonType: b.ButtonType || b.buttonType || null,
        nextNodeId: b.NextNodeId || b.nextNodeId || null,
        orderNo: b.OrderNo || b.orderNo || null,
        isActive: b.isActive !== false && b.IsActive !== false,
        link: b.Link || b.link || null,
        isFooter: !!(b.IsFooter || b.isFooter),
        proTypeId: b.proTypeId || b.ProTypeId || null,
      }));
      const apiLogIds = selectedApiLogIds?.length > 0 ? selectedApiLogIds.map(Number) : [];
      
      // Build header object with proper structure
      let headerObj = null;
      if (headerText1.trim() || headerText2.trim() || headerText3.trim() || headerBtnText.trim() || headerBtnType.trim() || headerBtnAction.trim() || headerBtnIcon.trim() || headerBtnNavigateTo.trim()) {
        headerObj = {
          text1: headerText1.trim() || null,
          text2: headerText2.trim() || null,
          text3: headerText3.trim() || null,
          button: null,
        };
        
        if (headerBtnText.trim() || headerBtnType.trim() || headerBtnAction.trim() || headerBtnIcon.trim() || headerBtnNavigateTo.trim()) {
          headerObj.button = {
            buttonText: headerBtnText.trim() || null,
            buttonType: headerBtnType.trim() || null,
            buttonAction: headerBtnAction.trim() || null,
            buttonIcon: headerBtnIcon.trim() || null,
            navigateTo: headerBtnNavigateTo.trim() || null,
            apiLogDetails: null,
          };
          
          // If navigateTo is selected, fetch the API log details and add as object (not string)
          if (headerBtnNavigateTo.trim()) {
            const selectedApi = apiOptions?.find(opt => String(opt.apiLogId || opt.value) === headerBtnNavigateTo.trim());
            if (selectedApi) {
              headerObj.button.apiLogDetails = {
                apiLogId: selectedApi.apiLogId || selectedApi.value,
                apiName: selectedApi.apiName || selectedApi.label,
                apiUrl: selectedApi.apiUrl || '',
                apiMethod: selectedApi.apiMethod || '',
                apiPayload: selectedApi.apiPayload || '',
                apiHeaders: selectedApi.apiHeaders || '',
                description: selectedApi.description || '',
                category: selectedApi.category || '',
                onLoad: selectedApi.onLoad || false,
                current_Life_stage: selectedApi.current_Life_stage || null,
                forChoosePath: selectedApi.forChoosePath || false,
              };
            }
          }
        }
      }
      
      await onSubmit(text.trim(), chosenType, finalButtons, apiLogIds, paymentResource.trim() || null, headerObj, sameScreenId || null);
      setText('');
      setButtons([]);
      setBtnText('');
      setBtnLink('');
      setBtnIsFooter(false);
      setEditingBtnIdx(null);
    } catch { setError({ name: 'Failed to save.' }); }
    finally { setLoading(false); }
  };

  const isEdit = !!editData;
  const title  = isEdit ? 'Edit Step' : 'Add Step';

  return (
    <Modal
      title={<span style={{ fontSize: 16, color: '#474747', fontWeight: "bold" }}>{title}</span>}
      visible={visible} onCancel={onClose} footer={null} centered destroyOnClose className="ag-modal" width={700} bodyStyle={{ padding: '20px 28px 28px', borderTop:"1px solid #bfbfbf" }}
    >
      {/* Node Type select — shown on edit or when nodeType not pre-selected */}
      {(isEdit || !nodeType) && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>
            Node Type <span style={{ color: '#720C20' }}>*</span>
          </label>
          <Select
            value={selectedType}
            onChange={(val) => { setSelectedType(val); if (error?.type) setError(p => ({ ...p, type: '' })); }}
            style={{ width: '100%' }}
            size="large"
            placeholder="Select Node Type"
          >
            {nodeTypes?.map(nt => (
              <Option key={nt?.value} value={nt?.value}>{nt?.label}</Option>
            ))}
          </Select>
          {error.type && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{error.type}</p>}
        </div>
      )}

      {/* Step Name */}
      <div style={{ marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>
          Step Name <span style={{ color: '#720C20' }}>*</span>
        </label>
      </div>
      <Input
        placeholder="e.g., Step name"
        value={text}
        onChange={e => { setText(e.target.value); if (error.name) setError(p => ({ ...p, name: '' })); }}
        onPressEnter={handleSubmit}
        style={{ borderRadius: 2, borderColor: error.name ? '#ff4d4f' : '#720C20', height: 40 }}
      />
      {error.name && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{error.name}</p>}

      {/* Api select */}
      <div style={{ marginTop: 16, marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>
          Select API For Screen <span style={{ fontSize: 11, color: '#999' }}>(optional)</span>
        </label>
      </div>
      <Select
        allowClear
        showSearch
        mode="multiple"
        placeholder="Select APIs"
        value={selectedApiLogIds}
        onChange={(val) => setSelectedApiLogIds(val)}
        loading={apiOptionsLoading}
        optionFilterProp="children"
        style={{ width: '100%' }}
        size="large"
        maxTagCount="responsive"
        suffixIcon={<span style={{ color: '#720C20' }}>▼</span>}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      >
        {apiOptions?.map(opt => (
          <Option key={opt?.apiLogId || opt?.value} value={String(opt?.apiLogId || opt?.value)}>{opt?.apiName || opt?.label}</Option>
        ))}
      </Select>

      {/* Payment Resource */}
      <div style={{ marginTop: 16, marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>
          Payment Resource <span style={{ fontSize: 11, color: '#999' }}>(optional)</span>
        </label>
      </div>
      <Input
        placeholder="e.g., LPO, Insurance, Self-pay"
        value={paymentResource}
        onChange={e => setPaymentResource(e.target.value)}
        style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 40 }}
      />

      {/* Screen Template */}
      <div style={{ marginTop: 16, marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>
          Screens Template <span style={{ fontSize: 11, color: '#999' }}>(optional)</span>
        </label>
      </div>
      <Select
        allowClear
        showSearch
        placeholder="Select screen template"
        value={sameScreenId != null ? sameScreenId : undefined}
        onChange={(val) => setSameScreenId(val)}
        onClear={() => setSameScreenId(null)}
        loading={screenTemplatesLoading}
        optionFilterProp="children"
        style={{ width: '100%' }}
        size="large"
      >
        {(() => {
          // For Info steps, restrict the screen templates to a fixed allowed set.
          // All other step types (Form, Action, Question, etc.) show every template.
          const INFO_ALLOWED_SCREEN_NAMES = [
            'Email Preview',
            'Text Preview',
            'Email & Text Preview',
            'Access Document',
          ];
          const currentTypeLabel = nodeTypes?.find(t => String(t.value) === String(selectedType))?.label;
          const isInfoStep = (currentTypeLabel || '').trim().toLowerCase() === 'info';
          const templatesToShow = isInfoStep
            ? screenTemplates?.filter(t =>
                INFO_ALLOWED_SCREEN_NAMES?.some(name =>
                  name.toLowerCase() === (t.screenName || '').trim().toLowerCase()
                )
              )
            : screenTemplates;
          return templatesToShow?.map(template => (
            <Option key={template.screenTemplateId} value={template.screenTemplateId}>{template.screenName}</Option>
          ));
        })()}
      </Select>

      {/* Buttons section */}
      <div style={{ marginTop: 20 }}>
        <label style={{ fontSize: 13, color: '#333', display: 'block', marginBottom: 6 }}>
          Buttons
        </label>
        {buttons?.filter(b => b.isActive !== false && b.IsActive !== false)?.length > 0 && (
          <div className='step-modal-btn-list'>
            {buttons?.filter(b => b.isActive !== false && b.IsActive !== false)?.map((btn, idx) => {
              const actualIdx = buttons.indexOf(btn);
              const hasLink = !!(btn.link || btn.Link);
              const isFooterBtn = !!(btn.isFooter || btn.IsFooter);
              const isEditing = editingBtnIdx === actualIdx;
              
              return (
                <div key={idx} className='step-modal-btn-item' style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, backgroundColor: isEditing ? '#fff9e6' : 'transparent', border: isEditing ? '1px solid #faad14' : '1px solid transparent', borderRadius: 4, padding: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className='step-modal-btn-text'>{btn.ButtonText || btn.buttonText}</span>
                        {isFooterBtn && (
                          <span style={{ fontSize: 10, padding: '2px 6px', backgroundColor: '#52c41a', color: '#fff', borderRadius: 3, fontWeight: 500 }}>
                            Footer
                          </span>
                        )}
                      </div>
                      {hasLink && (
                        <span style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                          Link: {btn.Link || btn.link}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Tooltip title="Edit">
                        <EditOutlined 
                          style={{ fontSize: 14, color: '#1890ff', cursor: 'pointer' }} 
                          onClick={() => handleEditButton(actualIdx)} 
                        />
                      </Tooltip>
                      <span className='step-modal-btn-remove' onClick={() => handleRemoveButton(actualIdx)}>✕</span>
                    </div>
                  </div>
                  {isEdit && !hasLink && (
                    <div>
                      <label style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 2 }}>Next Node (optional)</label>
                      <Select
                        allowClear
                        showSearch
                        placeholder="Select next node"
                        value={btn.nextNodeId || btn.NextNodeId || undefined}
                        onChange={(val) => {
                          setButtons(prev => prev?.map((b, i) => i === actualIdx ? { ...b, nextNodeId: val != null ? val : null, NextNodeId: val != null ? val : null } : b));
                        }}
                        onClear={() => {
                          setButtons(prev => prev?.map((b, i) => i === actualIdx ? { ...b, nextNodeId: null, NextNodeId: null } : b));
                        }}
                        optionFilterProp="children"
                        style={{ width: '100%' }}
                        size="small"
                        dropdownStyle={{ minWidth: 500 }}
                        dropdownMatchSelectWidth={false}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                      >
                        {nextNodeOptions.map(n => (
                          <Option key={n.nodeId} value={n.nodeId}>{n.name} ({n.nodeTypeName})</Option>
                        ))}
                      </Select>
                    </div>
                  )}
                  {isEdit && hasLink && (
                    <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>
                      (Button has link - Next Node not applicable)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              placeholder="Button name"
              value={btnText}
              onChange={e => setBtnText(e.target.value)}
              onPressEnter={handleAddButton}
              style={{ borderRadius: 2, borderColor: '#720C20', height: 36, flex: 1 }}
            />
            <Button
              onClick={handleAddButton}
              disabled={!btnText.trim()}
              style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 2, fontWeight: 500, height: 36, minWidth: 80 }}
            >
              {editingBtnIdx !== null ? 'Update' : '+ Add'}
            </Button>
            {editingBtnIdx !== null && (
              <Button
                onClick={handleCancelEditButton}
                style={{ borderColor: '#d9d9d9', color: '#595959', borderRadius: 2, fontWeight: 500, height: 36 }}
              >
                Cancel
              </Button>
            )}
          </div>
          <Input
            placeholder="Link (optional - for external URLs or routes)"
            value={btnLink}
            onChange={e => setBtnLink(e.target.value)}
            style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
          />
          
          {/* Is Footer Button with explanation */}
          <div style={{ marginTop: 8, marginBottom: 4, padding: '8px 10px', backgroundColor: '#fff2f0', borderRadius: 4, border: '1px solid #ffccc7' }}>
            <div style={{ fontSize: 11, color: '#cf1322', lineHeight: 1.5 }}>
              <strong>Footer Button Position:</strong> If set to <strong>True</strong>, the button appears in the <strong>bottom-right corner or in the center of bottom</strong> of the screen. If <strong>False</strong>, it displays in the <strong>center</strong> of the screen.
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 13, color: '#333', marginBottom: 0 }}>Is Footer Button:</label>
            <Select
              value={btnIsFooter}
              onChange={(val) => setBtnIsFooter(val)}
              style={{ width: 100 }}
              size="small"
            >
              <Option value={true}>True</Option>
              <Option value={false}>False</Option>
            </Select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, color: '#333', marginBottom: 4, display: 'block' }}>Professional Service Category:</label>
              <Select
                allowClear
                showSearch
                placeholder="Select service category"
                value={btnProSerDescId}
                onChange={(val) => { 
                  setBtnProSerDescId(val); 
                  setBtnProTypeId(undefined);
                  if (val) fetchProfessionalTypes(val);
                  else setProTypeOptions([]);
                }}
                optionFilterProp="children"
                style={{ width: '100%' }}
                size="small"
              >
                {proSerDescOptions?.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, color: '#333', marginBottom: 4, display: 'block' }}>Professional Type:</label>
              <Select
                allowClear
                showSearch
                placeholder="Select professional type"
                value={btnProTypeId}
                onChange={(val) => setBtnProTypeId(val)}
                disabled={!btnProSerDescId}
                loading={proTypeLoading}
                optionFilterProp="children"
                style={{ width: '100%' }}
                size="small"
              >
                {proTypeOptions?.map(opt => (
                  <Option key={opt.value} value={Number(opt.value)}>{opt.label}</Option>
                ))}
              </Select>
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#666', margin: 0 }}>
            Note: If you add a link, the button will navigate to that URL. Otherwise, you can select a Next Node after adding the button. Footer buttons appear at the bottom of the screen.
          </p>
        </div>
      </div>

      {/* Header section - COMMENTED OUT FOR NOW */}
      {/*
      <div style={{ marginTop: 20, border: '1px solid #e8e8e8', borderRadius: 4, padding: '12px 14px' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#474747', display: 'block', marginBottom: 12 }}>
          Header <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>(optional)</span>
        </label>

        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Text 1</label>
            <Input
              placeholder="e.g., Welcome"
              value={headerText1}
              onChange={e => setHeaderText1(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Text 2</label>
            <Input
              placeholder="e.g., Subtitle"
              value={headerText2}
              onChange={e => setHeaderText2(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Text 3</label>
            <Input
              placeholder="e.g., Description"
              value={headerText3}
              onChange={e => setHeaderText3(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 8 }}>Header Button</label>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Button Text</label>
            <Input
              placeholder="e.g., Get Started"
              value={headerBtnText}
              onChange={e => setHeaderBtnText(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Button Type</label>
            <Input
              placeholder="e.g., primary"
              value={headerBtnType}
              onChange={e => setHeaderBtnType(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Button Action</label>
            <Input
              placeholder="e.g., navigate"
              value={headerBtnAction}
              onChange={e => setHeaderBtnAction(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Button Icon</label>
            <Input
              placeholder="e.g., arrow-right"
              value={headerBtnIcon}
              onChange={e => setHeaderBtnIcon(e.target.value)}
              style={{ borderRadius: 2, borderColor: '#d9d9d9', height: 36 }}
            />
          </div>
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Navigate To</label>
          <Select
            allowClear
            showSearch
            placeholder="e.g., /next-screen"
            value={headerBtnNavigateTo ? String(headerBtnNavigateTo) : undefined}
            onChange={(val) => setHeaderBtnNavigateTo(val || '')}
            loading={apiOptionsLoading}
            optionFilterProp="children"
            style={{ width: '100%' }}
            size="small"
            dropdownStyle={{ minWidth: 400 }}
            dropdownMatchSelectWidth={false}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
          >
            {apiOptions?.map(opt => (
              <Option key={opt?.apiLogId || opt?.value} value={String(opt?.apiLogId || opt?.value)}>{opt?.apiName || opt?.label}</Option>
            ))}
          </Select>
        </div>
      </div>
      */}

      <div style={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end', alignItems: 'center', gap: 10, marginTop: 24 }}>
        {isEdit && onDelete && (
          <Button
            danger
            icon={<DeleteOutlined color='rgb(114, 12, 32)' />}
            onClick={() => setConfirmVisible(true)}
            disabled={loading}
            style={{
              borderRadius: 2,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              // gap: 4,
              borderColor: "rgb(114, 12, 32)",
              color: "rgb(114, 12, 32)"
            }}
          >
            Delete
          </Button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button onClick={onClose} disabled={loading} style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 2, fontWeight: 500 }}>Cancel</Button>
          <Button loading={loading} onClick={handleSubmit} style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', borderRadius: 2, fontWeight: 500 }}>
            {isEdit ? 'Update Step' : 'Add Step'}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={420}
        zIndex={1200}
        closable
        className="ag-modal"
        bodyStyle={{ padding: '28px 28px 20px' }}
      >
        <p style={{ fontSize: 15, color: '#333', marginBottom: 20, textAlign: 'center' }}>
          Are you sure you want to delete <strong>{editData?.name}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={() => setConfirmVisible(false)} style={{ minWidth: 100, height: 36, borderRadius: 4, borderColor: '#720C20', color: '#720C20' }}>Cancel</Button>
          <Button
            onClick={() => { setConfirmVisible(false); onDelete(editData.nodeId); }}
            style={{ minWidth: 100, height: 36, borderRadius: 4, backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff' }}
          >Delete</Button>
        </div>
      </Modal>
    </Modal>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────
const AgentTree = ({ categories, nodeTypes, onEditCat, onAddCat, loggedUserId, onNotify, onError }) => {
  return (
    <div className='agt-tree'>
      {categories?.map(cat => (
        <CategoryBlock
          key={cat?.categoryId}
          cat={cat}
          nodeTypes={nodeTypes}
          loggedUserId={loggedUserId}
          onNotify={onNotify}

          onError={onError}

          onEditCat={onEditCat}
        />
      ))}

      {/* Add Service Category */}
      <div className='agt-add-cat-btn' onClick={onAddCat}>
        <PlusOutlined style={{ fontSize: 11 }} /> Add Service Category
      </div>
    </div>
  );
};

export default AgentTree;






